import { CurrencyCode } from '@reach/shared-types/currency';
import {
  ExposureEnum,
  type FieldExposure,
  type SchemaDisplayMetadata,
} from '@reach/shared-types/schema-mappings';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Braces, Check, FileJson, Loader2, Plus, Save, Trash2, Undo2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAllFlags } from '../hooks/useAllFlags';
import type {
  ApiError,
  ContactSourceSchema,
  SchemaDefinition,
  SchemaDefinitionUpdate,
  SchemaMappings as SchemaMappingsType,
} from '../lib/api';
import { partnerApi } from '../lib/api';
import { schemaDefinitionKeys } from '../lib/schema-definition-query-keys';
import { ConfirmDialog } from './common/ConfirmDialog';
import { CreateSchemaDialog } from './CreateSchemaDialog';
import {
  buildFieldsToExposeFromState,
  buildFieldsToExposeUpdate,
  hasFieldExposure,
  hydrateFieldExposure,
  jsonPathToTopLevelPropertyName,
  parseSchemaLookupToolConflict,
  removeFieldExposure,
  removeSchemaField,
  schemaExposureLookupConflicts,
  type FieldExposureState,
  type SchemaLookupConflict,
} from './schemaFieldRemoval';
import { preserveMappingMetadata } from './schemaMappingMetadata';
import { SchemaIconColorPicker } from './schema-display/SchemaIconColorPicker';
import { SchemaLookupConflictList } from './SchemaLookupConflictList';
import { AttributeEditorPanel } from './schema-editor/AttributeEditorPanel';
import { AttributesTable } from './schema-editor/AttributesTable';
import { resolveReferenceLabel } from './schema-editor/helpers';
import { SchemaEmptyState } from './schema-editor/SchemaEmptyState';
import { SchemaJsonDialog } from './schema-editor/SchemaJsonDialog';
import { SchemaMetaCard } from './schema-editor/SchemaMetaCard';
import { SchemaSwitcher } from './schema-editor/SchemaSwitcher';
import styles from './SchemaDefinitionsWithMappings.module.css';

type JsonSchemaType = 'string' | 'number' | 'integer' | 'boolean' | 'array' | 'object' | 'reference';

type ArrayItemType = Exclude<JsonSchemaType, 'array'>;

type JsonSchemaFormat =
  | 'date-time'
  | 'date'
  | 'time'
  | 'email'
  | 'uri'
  | 'uri-reference'
  | 'uuid'
  | 'hostname'
  | 'ipv4'
  | 'ipv6';

const SCHEMA_MAPPING_METADATA_KEYS = ['urlPattern'] as const;

// Shared empty set so a clean draft doesn't allocate one per render.
const EMPTY_NAME_SET: ReadonlySet<string> = new Set<string>();

interface SchemaProperty {
  name: string;
  type: JsonSchemaType;
  description: string;
  format?: JsonSchemaFormat;
  required: boolean;
  enum?: string[];
  $ref?: string;
  // Array items configuration
  itemsType?: ArrayItemType;
  itemsRef?: string;
  itemsFormat?: JsonSchemaFormat;
}

interface FieldMapping {
  fieldName: string;
  mappingTypes: string[];
  jsonPath: string;
}

interface EditableField {
  name: string;
  type: JsonSchemaType;
  description: string;
  format?: JsonSchemaFormat;
  required: boolean;
  enum?: string[];
  enumInput?: string; // Raw input string for enum
  isEditing?: boolean;
  $ref?: string;
  // Array items configuration
  itemsType?: ArrayItemType;
  itemsRef?: string;
  itemsFormat?: JsonSchemaFormat;
}

/**
 * Stable fingerprint of everything the attributes table and the editor panel can
 * change about a single attribute. Comparing it against the fingerprint taken at
 * the last save is what marks an individual row as having unsaved edits.
 */
function attributeSignature(
  field: EditableField,
  mapping: FieldMapping | undefined,
  exposure: FieldExposureState[string] | undefined,
): string {
  return JSON.stringify([
    field.type,
    field.format ?? '',
    field.required,
    field.enum ?? [],
    field.$ref ?? '',
    field.itemsType ?? '',
    field.itemsRef ?? '',
    field.itemsFormat ?? '',
    field.description ?? '',
    (mapping?.mappingTypes ?? []).filter(type => type !== 'none').sort(),
    mapping?.jsonPath ?? '',
    exposure?.segments ?? false,
    exposure?.mergeFields ?? false,
    exposure?.agentTools ?? false,
    exposure?.name ?? '',
    exposure?.description ?? '',
  ]);
}

interface ApiErrorConflict {
  field: string;
  resourceExternalIds?: string[];
  invalidValues?: string[];
  values?: string[];
  existingValues?: string[];
  conflictingValues?: string[];
  resourceCount?: number;
  count?: number;
  affectedResources?: number;
}

interface FieldsToExposeConflict {
  fieldName: string;
  usageType: FieldExposure;
  segmentIds?: string[];
  communicationGroupIds?: string[];
  automationIds?: string[];
  toolDefinitionIds?: string[];
}

interface ApiErrorData {
  error?:
    | string
    | {
        error?: string;
        message?: string;
        conflicts?: ApiErrorConflict[];
        fieldsToExposeConflicts?: FieldsToExposeConflict[];
      };
  message?: string;
  errors?: Array<{
    path?: string[];
    message: string;
    code?: string;
  }>;
  data?: {
    error?: {
      error?: string;
      message?: string;
      conflicts?: ApiErrorConflict[];
      fieldsToExposeConflicts?: FieldsToExposeConflict[];
    };
  };
}

interface TransactionAmount {
  field: string;
  currency?: string;
  currencyField?: string;
}

interface StatusField {
  field: string;
  activeValue?: string;
  deactivatedValue?: string;
}

interface FieldToExpose {
  name: string;
  description?: string;
  jsonPath: string;
  exposure: FieldExposure[];
}

// Schema mapping types are now derived dynamically based on schema category

export const SchemaDefinitionsWithMappings: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { ld } = useAllFlags();
  const agentToolsEnabled = ld['enable-partner-mcp-authoring'] ?? false;

  // Initialize selectedSchemaId from query params
  const [selectedSchemaId, setSelectedSchemaId] = useState<string>(() => {
    return searchParams.get('schema') || '';
  });
  // The schema editor is always editable — there is no edit-mode gate. Instead we
  // track whether the draft diverges from the server so Save/Discard can enable.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const markDirty = useCallback(() => setHasUnsavedChanges(true), []);
  // Attribute fingerprints as of the last clean draft, keyed by field name.
  const savedAttributeSignaturesRef = useRef<Record<string, string>>({});
  // Tracks the last (schemaId:version) we initialized the draft from, so the
  // initializer runs exactly once per schema load. Without this, a React Query
  // background refetch would re-run and clobber the user's in-progress edits.
  const initializedKeyRef = useRef<string | null>(null);
  // Set when the user edits transaction filter conditions so the sync effect only
  // pushes into editedMappings on real interaction, not on initial hydration.
  const filtersDirtyRef = useRef(false);
  // The attribute editor panel edits one attribute at a time; null = panel closed.
  const [selectedFieldName, setSelectedFieldName] = useState<string | null>(null);
  // When the user tries to switch schemas with unsaved edits we stash the target
  // here and show a confirm dialog before discarding. '' means "deselect".
  const [pendingSchemaSwitch, setPendingSchemaSwitch] = useState<string | null>(null);
  const [editedSchema, setEditedSchema] = useState<SchemaDefinition | null>(null);
  const [editedMappings, setEditedMappings] = useState<SchemaMappingsType | null>(null);
  const [error, setError] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [schemaError, setSchemaError] = useState<string>('');
  const [schemaSuccessMessage, setSchemaSuccessMessage] = useState<string>('');
  const [mappingError, setMappingError] = useState<string>('');
  const [mappingErrorDetails, setMappingErrorDetails] = useState<string[]>([]);
  const [schemaLookupConflicts, setSchemaLookupConflicts] = useState<SchemaLookupConflict[]>([]);
  const [mappingLookupConflicts, setMappingLookupConflicts] = useState<SchemaLookupConflict[]>([]);
  const [mappingSuccessMessage, setMappingSuccessMessage] = useState<string>('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeactivateDialogOpen, setIsDeactivateDialogOpen] = useState(false);
  const [isJsonDialogOpen, setIsJsonDialogOpen] = useState(false);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [editableFields, setEditableFields] = useState<EditableField[]>([]);
  const [fieldExposure, setFieldExposure] = useState<FieldExposureState>({});
  // Filter text for the attributes table.
  const [attributeQuery, setAttributeQuery] = useState('');
  const [conflictingFields, setConflictingFields] = useState<
    Array<{ field: string; hasResources: boolean }>
  >([]);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [contactSourceType, setContactSourceType] = useState<'primary' | 'additional'>('primary');
  const [additionalSourceIndex, setAdditionalSourceIndex] = useState<number>(0);
  const [urlPatternInput, setUrlPatternInput] = useState<string>('');
  const [fieldPendingRemoval, setFieldPendingRemoval] = useState<EditableField | null>(null);
  const [showAssignSchemaDialog, setShowAssignSchemaDialog] = useState(false);
  const [assignmentType, setAssignmentType] = useState<'primary' | 'additional'>('primary');
  const [pendingAssignment, setPendingAssignment] = useState<'primary' | 'additional' | null>(null);
  const [showSwapWarning, setShowSwapWarning] = useState(false);

  // Conditional filter state
  const [filterConditions, setFilterConditions] = useState<
    Array<{
      id: string;
      field: string;
      operator: string;
      value: string;
    }>
  >([]);
  const [filterLogic, setFilterLogic] = useState<'and' | 'or'>('and');

  // Filter management functions
  const addFilterCondition = () => {
    const newCondition = {
      id: `condition_${Date.now()}`,
      field: 'status',
      operator: '==',
      value: '',
    };
    filtersDirtyRef.current = true;
    markDirty();
    setFilterConditions([...filterConditions, newCondition]);
  };

  const updateFilterCondition = (
    id: string,
    updates: Partial<{
      field: string;
      operator: string;
      value: string;
    }>,
  ) => {
    filtersDirtyRef.current = true;
    markDirty();
    setFilterConditions(prev =>
      prev.map(condition => (condition.id === id ? { ...condition, ...updates } : condition)),
    );
  };

  const removeFilterCondition = (id: string) => {
    filtersDirtyRef.current = true;
    markDirty();
    setFilterConditions(prev => prev.filter(condition => condition.id !== id));
  };

  const handleFilterLogicChange = (logic: 'and' | 'or') => {
    filtersDirtyRef.current = true;
    markDirty();
    setFilterLogic(logic);
  };

  // Get display text for operator
  // Fetch schemas
  const { data: schemas = [], isLoading: schemasLoading } = useQuery({
    queryKey: schemaDefinitionKeys.list(),
    queryFn: () => partnerApi.getSchemaDefinitions(),
  });

  // Sync selectedSchemaId with query params when they change
  useEffect(() => {
    const schemaParam = searchParams.get('schema');
    if (schemaParam && schemaParam !== selectedSchemaId) {
      setSelectedSchemaId(schemaParam);
    }
  }, [searchParams, selectedSchemaId]);

  // Fetch selected schema
  const { data: selectedSchema, isLoading: schemaLoading } = useQuery({
    queryKey: schemaDefinitionKeys.detail(selectedSchemaId),
    queryFn: () => partnerApi.getSchemaDefinition(selectedSchemaId),
    enabled: !!selectedSchemaId,
  });

  // Fetch mappings
  const { data: mappings, isLoading: mappingsLoading } = useQuery({
    queryKey: ['schemaMappings'],
    queryFn: () => partnerApi.getSchemaMappings(),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: partnerApi.createSchemaDefinition.bind(partnerApi),
    onSuccess: (data: SchemaDefinition) => {
      queryClient.invalidateQueries({ queryKey: schemaDefinitionKeys.list() });
      queryClient.invalidateQueries({ queryKey: ['availableSegmentFieldsWithTypes'] });
      setIsCreateDialogOpen(false);
      setSelectedSchemaId(data.id);
      setSuccessMessage('Schema created successfully!');
      setError('');
      setErrorDetails([]);
      setTimeout(() => setSuccessMessage(''), 5000);
    },
    onError: (err: ApiError) => {
      const errorMessage = parseApiError(err);
      setError(errorMessage.message);
      setErrorDetails(errorMessage.details);
      setSuccessMessage('');
    },
  });

  // Deactivate mutation
  const deactivateMutation = useMutation({
    mutationFn: (schemaId: string) => partnerApi.deleteSchemaDefinition(schemaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schemaDefinitionKeys.list() });
      queryClient.invalidateQueries({ queryKey: ['availableSegmentFieldsWithTypes'] });
      setIsDeactivateDialogOpen(false);
      setSelectedSchemaId('');
      setSelectedFieldName(null);
      setHasUnsavedChanges(false);
      // The deactivated schema is gone, so drop it from the URL or the sync effect reselects it
      const paramsWithoutSchema = new URLSearchParams(searchParams);
      paramsWithoutSchema.delete('schema');
      setSearchParams(paramsWithoutSchema, { replace: true });
      setSuccessMessage('Schema deactivated successfully. Its name can now be reused.');
      setError('');
      setErrorDetails([]);
      setTimeout(() => setSuccessMessage(''), 5000);
    },
    onError: (err: ApiError) => {
      const errorMessage = parseApiError(err);
      setIsDeactivateDialogOpen(false);
      setError(errorMessage.message);
      setErrorDetails(errorMessage.details);
      setSuccessMessage('');
    },
  });

  // Update schema mutation
  const updateSchemaMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: SchemaDefinitionUpdate }) =>
      partnerApi.updateSchemaDefinition(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: schemaDefinitionKeys.detail(selectedSchemaId),
      });
      queryClient.invalidateQueries({ queryKey: schemaDefinitionKeys.list() });
      queryClient.invalidateQueries({ queryKey: ['availableSegmentFieldsWithTypes'] });
      setHasUnsavedChanges(false);
      setSchemaSuccessMessage('Schema updated successfully!');
      setSchemaError('');
      setSchemaLookupConflicts([]);
      setTimeout(() => setSchemaSuccessMessage(''), 5000);
    },
    onError: (err: ApiError) => {
      const errorMessage = parseApiError(err);

      // Check if this is a conflict error with fields that have no resources
      const responseData = err.response?.data as ApiErrorData;
      const dataError = responseData?.data?.error;

      const lookupConflicts = schemaExposureLookupConflicts(dataError);
      setSchemaLookupConflicts(lookupConflicts);

      if (
        dataError &&
        typeof dataError === 'object' &&
        'error' in dataError &&
        typeof dataError.error === 'string' &&
        dataError.error.includes('SCHEMA_MAPPING_FIELD_CONFLICT')
      ) {
        const conflicts = dataError.conflicts || [];
        const fieldsWithoutResources = conflicts.filter(
          (c: ApiErrorConflict) => !c.resourceExternalIds || c.resourceExternalIds.length === 0,
        );
        const fieldsWithResources = conflicts.filter(
          (c: ApiErrorConflict) => c.resourceExternalIds && c.resourceExternalIds.length > 0,
        );

        // If all conflicting fields have no resources, we can offer to auto-fix
        if (fieldsWithoutResources.length > 0 && fieldsWithResources.length === 0) {
          setConflictingFields(
            fieldsWithoutResources.map((c: ApiErrorConflict) => ({
              field: c.field,
              hasResources: false,
            })),
          );
          setShowConflictDialog(true);
          return;
        }

        // If some fields have resources, show the error normally
        if (fieldsWithResources.length > 0) {
          setConflictingFields(
            fieldsWithResources.map((c: ApiErrorConflict) => ({
              field: c.field,
              hasResources: true,
            })),
          );
        }
      }

      setSchemaError(
        lookupConflicts.length
          ? 'At least one returned field is required by an active schema lookup.'
          : errorMessage.message,
      );
      setErrorDetails(errorMessage.details);
      setSchemaSuccessMessage('');

      // Invalidate and refetch the schema to get the current state from the server
      queryClient.invalidateQueries({
        queryKey: schemaDefinitionKeys.detail(selectedSchemaId),
      });

      // The query will refetch automatically and when it completes,
      // the useEffect will reinitialize the editable state
      // Don't exit edit mode on error so user can fix the issues
    },
  });

  const handleApplyDisplayMetadata = (displayMetadata: SchemaDisplayMetadata) => {
    if (!selectedSchema) return;
    updateSchemaMutation.mutate({ id: selectedSchema.id, data: { displayMetadata } });
  };

  // Update mappings mutation
  const updateMappingsMutation = useMutation({
    mutationFn: (data: SchemaMappingsType) => partnerApi.updateSchemaMappings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schemaMappings'] });
      setMappingSuccessMessage('Mappings updated successfully!');
      setMappingError('');
      setMappingErrorDetails([]);
      setMappingLookupConflicts([]);
      setPendingAssignment(null); // Clear pending assignment on successful save
      setHasUnsavedChanges(false);
      setTimeout(() => setMappingSuccessMessage(''), 5000);
    },
    onError: (err: ApiError) => {
      const errorMessage = parseApiError(err);
      const dataError = (err.response?.data as ApiErrorData)?.data?.error;
      const lookupToolConflict = parseSchemaLookupToolConflict(dataError);

      setMappingLookupConflicts(lookupToolConflict?.conflicts ?? []);
      setMappingError(lookupToolConflict?.message ?? errorMessage.message);
      setMappingErrorDetails(errorMessage.details);
      setMappingSuccessMessage('');
    },
  });

  // Helper function to process fieldsToExposeConflicts and add details
  const processFieldsToExposeConflicts = (
    conflicts: FieldsToExposeConflict[],
    details: string[],
  ): void => {
    conflicts.forEach((conflict: FieldsToExposeConflict) => {
      if (conflict.usageType === ExposureEnum.SEGMENTS) {
        const segmentCount = conflict.segmentIds?.length || 0;
        details.push(`Field "${conflict.fieldName}" is used in ${segmentCount} segment(s)`);
      } else if (conflict.usageType === ExposureEnum.MERGE_FIELDS) {
        const groupCount = conflict.communicationGroupIds?.length || 0;
        const automationCount = conflict.automationIds?.length || 0;
        const totalCount = groupCount + automationCount;
        if (totalCount > 0) {
          const parts: string[] = [];
          if (groupCount > 0) {
            parts.push(`${groupCount} communication group(s)`);
          }
          if (automationCount > 0) {
            parts.push(`${automationCount} automation(s)`);
          }
          details.push(
            `Field "${conflict.fieldName}" is used in merge fields by ${parts.join(' and ')}`,
          );
        } else {
          details.push(`Field "${conflict.fieldName}" is used in merge fields`);
        }
      }
    });
  };

  // Parse API errors
  const parseApiError = (
    err: ApiError,
  ): {
    message: string;
    details: string[];
  } => {
    const details: string[] = [];
    let message = 'An error occurred';

    if (err.response?.data) {
      const responseData = err.response.data;

      // Handle schema mapping field conflict errors
      const dataError = (responseData as ApiErrorData)?.data?.error;
      const schemaLookupToolConflict = parseSchemaLookupToolConflict(dataError);

      if (schemaLookupToolConflict) {
        return { message: schemaLookupToolConflict.message, details: [] };
      }

      if (
        dataError &&
        typeof dataError === 'object' &&
        'error' in dataError &&
        typeof dataError.error === 'string'
      ) {
        if (dataError.error.includes('SCHEMA_MAPPING_FIELD_CONFLICT')) {
          message =
            dataError.message ||
            'Cannot update schema because some fields are used in platform schema mappings';

          if (dataError.conflicts && Array.isArray(dataError.conflicts)) {
            dataError.conflicts.forEach(
              (conflict: { field: string; resourceExternalIds?: string[] }) => {
                const conflictDetail =
                  conflict.resourceExternalIds && conflict.resourceExternalIds.length > 0
                    ? `Field "${conflict.field}" is mapped and used by ${conflict.resourceExternalIds.length} resource(s)`
                    : `Field "${conflict.field}" is mapped and cannot be modified`;
                details.push(conflictDetail);
              },
            );
          }

          return { message, details };
        } else if (dataError.error.includes('ENUM_VALUE_CONFLICT')) {
          message =
            dataError.message ||
            'Cannot modify enum constraints because existing resources contain values that would no longer be valid';

          if (dataError.conflicts && Array.isArray(dataError.conflicts)) {
            dataError.conflicts.forEach((conflict: ApiErrorConflict) => {
              const fieldName = conflict.field || 'unknown field';
              let conflictDetail = `Field "${fieldName}": `;

              // Try different possible property names for the invalid values
              const invalidValues =
                conflict.invalidValues ||
                conflict.values ||
                conflict.existingValues ||
                conflict.conflictingValues;
              const resourceCount =
                conflict.resourceCount || conflict.count || conflict.affectedResources;

              if (invalidValues && Array.isArray(invalidValues)) {
                const valuesList = invalidValues.join(', ');
                conflictDetail += `Existing resources have values [${valuesList}] that are not in the new enum`;
              } else if (resourceCount) {
                conflictDetail += `${resourceCount} resource(s) have values that are not in the new enum`;
              } else {
                conflictDetail += 'Existing resources have values that are not in the new enum';
              }

              details.push(conflictDetail);
            });
          }

          return { message, details };
        } else if (
          dataError.fieldsToExposeConflicts ||
          (dataError.error &&
            typeof dataError.error === 'string' &&
            (dataError.error.toLowerCase().includes('fieldstoexpose') ||
              dataError.error.toLowerCase().includes('fields to expose')))
        ) {
          // Use the message from the error if available
          message =
            dataError.message ||
            'Cannot remove or change fieldsToExpose that are in use by segments or merge fields';

          // Handle fieldsToExposeConflicts
          if (dataError.fieldsToExposeConflicts && Array.isArray(dataError.fieldsToExposeConflicts)) {
            processFieldsToExposeConflicts(dataError.fieldsToExposeConflicts, details);
          }

          return { message, details };
        }
      }

      // Handle standard error format
      if ((responseData as Record<string, unknown>).error) {
        const errorValue = (responseData as Record<string, unknown>).error;

        // Check if error is an object with nested error info
        if (typeof errorValue === 'object' && errorValue !== null) {
          const errorObj = errorValue as {
            error?: string;
            message?: string;
            conflicts?: ApiErrorConflict[];
            fieldsToExposeConflicts?: FieldsToExposeConflict[];
          };
          if (
            errorObj.error &&
            typeof errorObj.error === 'string' &&
            errorObj.error.includes('SCHEMA_MAPPING_FIELD_CONFLICT')
          ) {
            message =
              errorObj.message ||
              'Cannot update schema because some fields are used in platform schema mappings';

            if (errorObj.conflicts && Array.isArray(errorObj.conflicts)) {
              errorObj.conflicts.forEach(
                (conflict: { field: string; resourceExternalIds?: string[] }) => {
                  const conflictDetail =
                    conflict.resourceExternalIds && conflict.resourceExternalIds.length > 0
                      ? `Field "${conflict.field}" is mapped and used by ${conflict.resourceExternalIds.length} resource(s)`
                      : `Field "${conflict.field}" is mapped and cannot be modified`;
                  details.push(conflictDetail);
                },
              );
            }

            return { message, details };
          } else if (
            errorObj.fieldsToExposeConflicts ||
            (errorObj.error &&
              typeof errorObj.error === 'string' &&
              (errorObj.error.toLowerCase().includes('fieldstoexpose') ||
                errorObj.error.toLowerCase().includes('fields to expose')))
          ) {
            // Handle fieldsToExpose conflicts in standard error format
            message =
              errorObj.message ||
              'Cannot remove or change fieldsToExpose that are in use by segments or merge fields';

            if (errorObj.fieldsToExposeConflicts && Array.isArray(errorObj.fieldsToExposeConflicts)) {
              processFieldsToExposeConflicts(errorObj.fieldsToExposeConflicts, details);
            }

            return { message, details };
          } else {
            message = errorObj.message || JSON.stringify(errorObj);
          }
        } else {
          message = errorValue as string;
        }
      } else if ((responseData as Record<string, unknown>).message) {
        message = (responseData as Record<string, unknown>).message as string;
      }

      const errors = (responseData as Record<string, unknown>).errors;
      if (errors && Array.isArray(errors)) {
        errors.forEach((validationError: { path?: string[]; message: string; code?: string }) => {
          const path = validationError.path?.join('.') || 'Unknown field';
          details.push(`${path}: ${validationError.message}`);
        });
      }
    } else if (err.message) {
      message = err.message;
    }

    return { message, details };
  };

  // Discards all in-progress edits by rehydrating the draft from the server.
  const handleDiscard = () => {
    setError('');
    setErrorDetails([]);
    setSchemaError('');
    setMappingError('');
    setSchemaLookupConflicts([]);
    setMappingLookupConflicts([]);
    setFieldPendingRemoval(null);
    setSelectedFieldName(null);
    initializeFromServer();
  };

  const initializeFieldMappings = useCallback(() => {
    if (!selectedSchema || !mappings) return;

    const schemaProperties = extractSchemaProperties(selectedSchema.schema);
    const newMappings: FieldMapping[] = [];
    const newEditableFields: EditableField[] = [];

    // Check if this schema is used in contacts mapping
    const contactsMapping = mappings.contactsSchema;
    let contactSource: ContactSourceSchema | null = null;

    if (contactsMapping) {
      // Check primary source
      // Match if schemaId matches OR if no schemaId is set AND this is a contacts schema
      if (
        contactsMapping.primarySource?.schemaId === selectedSchema.id ||
        (!contactsMapping.primarySource?.schemaId && selectedSchema.category === 'contacts_schema')
      ) {
        contactSource = contactsMapping.primarySource;
        setContactSourceType('primary');
      }
      // Check additional sources
      else if (contactsMapping.additionalSources) {
        const additionalIndex = contactsMapping.additionalSources.findIndex(
          src => src.schemaId === selectedSchema.id,
        );
        if (additionalIndex !== -1) {
          contactSource = contactsMapping.additionalSources[additionalIndex];
          setContactSourceType('additional');
          setAdditionalSourceIndex(additionalIndex);
        }
      }
    }

    if (contactSource) {
      schemaProperties.forEach(prop => {
        // Get ALL mapping types for this field from the API response
        const mappingTypes = getAllMappingTypesForField(prop.name, contactSource!);
        newMappings.push({
          fieldName: prop.name,
          mappingTypes,
          jsonPath: prop.name,
        });
      });
    } else if (selectedSchema.category === 'locations_schema' && mappings.locationsSchema) {
      // Check if this schema is used in locations mapping
      const locationMapping = mappings.locationsSchema.find(loc => loc.schemaId === selectedSchema.id);

      schemaProperties.forEach(prop => {
        const mappingTypes: string[] = [];

        if (locationMapping) {
          // Check if this field is mapped to id_field first
          if (locationMapping.id_field === prop.name) {
            mappingTypes.push('id_field');
          }

          // Check if this property is mapped to any other location field
          Object.keys(locationMapping).forEach(key => {
            if (
              key !== 'type' &&
              key !== 'schemaId' &&
              key !== 'id_field' &&
              (locationMapping as Record<string, unknown>)[key] === prop.name &&
              !mappingTypes.includes(key)
            ) {
              mappingTypes.push(key);
            }
          });
        }

        newMappings.push({
          fieldName: prop.name,
          mappingTypes: mappingTypes.length > 0 ? mappingTypes : ['none'],
          jsonPath: prop.name,
        });
      });
    } else if (selectedSchema.category === 'transactions_schema' && mappings.transactionsSchema) {
      // Check if this schema is used in transactions mapping
      const transactionMapping = mappings.transactionsSchema.find(
        txn => txn.schemaId === selectedSchema.id,
      );

      schemaProperties.forEach(prop => {
        const mappingTypes: string[] = [];

        if (transactionMapping) {
          // Check if this field is mapped to id_field or userId
          if (transactionMapping.id_field === prop.name) {
            mappingTypes.push('id_field');
          }
          if (transactionMapping.userId === prop.name) {
            mappingTypes.push('userId');
          }

          // Check if this property is mapped to any other transaction field
          Object.keys(transactionMapping).forEach(key => {
            if (
              key !== 'type' &&
              key !== 'schemaId' &&
              key !== 'eventType' &&
              key !== 'filter' &&
              key !== 'urlPattern' &&
              key !== 'id_field' &&
              key !== 'userId'
            ) {
              const value = (transactionMapping as Record<string, unknown>)[key];
              if (typeof value === 'string' && value === prop.name) {
                if (!mappingTypes.includes(key)) {
                  mappingTypes.push(key);
                }
              } else if (typeof value === 'object' && value !== null) {
                // Handle nested objects like dates and amount
                Object.keys(value).forEach(nestedKey => {
                  if ((value as Record<string, unknown>)[nestedKey] === prop.name) {
                    // For amount object, check if this is the 'field' property
                    if (key === 'amount' && nestedKey === 'field') {
                      if (!mappingTypes.includes('amount')) {
                        mappingTypes.push('amount');
                      }
                    } else {
                      const mappingKey = `${key}.${nestedKey}`;
                      if (!mappingTypes.includes(mappingKey)) {
                        mappingTypes.push(mappingKey);
                      }
                    }
                  }
                });
              }
            }
          });
        }

        newMappings.push({
          fieldName: prop.name,
          mappingTypes: mappingTypes.length > 0 ? mappingTypes : ['none'],
          jsonPath: prop.name,
        });
      });
    } else {
      // Initialize with no mappings - only use API response, no assumptions
      schemaProperties.forEach(prop => {
        newMappings.push({
          fieldName: prop.name,
          mappingTypes: ['none'],
          jsonPath: `$.${prop.name}`,
        });
      });
    }

    // Initialize editable fields
    schemaProperties.forEach(prop => {
      newEditableFields.push({
        name: prop.name,
        type: prop.type,
        description: prop.description,
        format: prop.format,
        required: prop.required,
        enum: prop.enum,
        $ref: prop.$ref,
        itemsType: prop.itemsType,
        itemsRef: prop.itemsRef,
        itemsFormat: prop.itemsFormat,
        isEditing: false,
      });
    });

    setFieldMappings(newMappings);
    setEditableFields(newEditableFields);
  }, [selectedSchema, mappings]);

  // Rehydrates the whole editable draft from the server-authoritative schema and
  // mappings, and clears the dirty flag. Called once per schema load and again on
  // Discard. Extracted so both paths share identical initialization.
  const initializeFromServer = useCallback(() => {
    if (!selectedSchema || !mappings) return;

    initializeFieldMappings();

    // Initialize field exposure from fieldsToExpose
    const fieldsToExpose = selectedSchema.fieldsToExpose ?? [];
    const exposureMap = hydrateFieldExposure(fieldsToExpose);
    setFieldExposure(exposureMap);

    // Update editableFields to include descriptions from fieldExposure where schema doesn't have one
    setEditableFields(prev => {
      return prev.map(field => {
        const exposure = exposureMap[field.name];
        if (exposure?.description && !field.description) {
          return { ...field, description: exposure.description };
        }
        return field;
      });
    });

    // The draft schema is always initialized (the editor is always editable).
    const schemaData = selectedSchema as unknown as Record<string, unknown>;
    setEditedSchema({
      ...selectedSchema,
      // Handle both camelCase and snake_case field names
      exposeNameJsonPath:
        selectedSchema.exposeNameJsonPath || (schemaData.json_path_expose_name as string | undefined),
    });

    // Reset any pending mapping/assignment draft and clear dirtiness.
    setEditedMappings(null);
    setPendingAssignment(null);
    setHasUnsavedChanges(false);
    filtersDirtyRef.current = false;

    // Initialize URL pattern input
    if (selectedSchema.category === 'contacts_schema' && mappings.contactsSchema) {
      const source =
        contactSourceType === 'primary'
          ? mappings.contactsSchema.primarySource
          : mappings.contactsSchema.additionalSources?.[additionalSourceIndex];
      setUrlPatternInput(source?.urlPattern || '');
    } else if (selectedSchema.category === 'transactions_schema' && mappings.transactionsSchema) {
      const txn = mappings.transactionsSchema.find(t => t.schemaId === selectedSchemaId);
      setUrlPatternInput(txn?.urlPattern || '');

      // Load existing filter conditions
      if (txn?.filter) {
        const filter = txn.filter as Record<string, unknown>;

        // Helper function to parse a condition and detect array_contains patterns
        const parseCondition = (
          condition: Record<string, unknown>,
          index: number,
        ): {
          id: string;
          field: string;
          operator: string;
          value: string;
        } => {
          // Check if this is an array_contains pattern (or with multiple in operations)
          if (condition.or && Array.isArray(condition.or)) {
            const orConditions = condition.or as Array<Record<string, unknown>>;
            // Check if all conditions are 'in' operations
            const allInOps = orConditions.every(cond => cond.in && Array.isArray(cond.in));

            if (allInOps && orConditions.length > 0) {
              // Extract field from first condition
              const firstIn = orConditions[0].in as [unknown, Record<string, unknown>];
              const fieldVar = firstIn[1];
              const fieldName = (fieldVar as Record<string, unknown>).var as string;

              // Check if all conditions reference the same field
              const sameField = orConditions.every(cond => {
                const inOp = cond.in as [unknown, Record<string, unknown>];
                return (inOp[1] as Record<string, unknown>).var === fieldName;
              });

              if (sameField) {
                // Extract all values
                const values = orConditions.map(cond => {
                  const inOp = cond.in as [unknown, Record<string, unknown>];
                  return String(inOp[0]);
                });
                return {
                  id: `condition_${Date.now()}_${index}`,
                  field: fieldName,
                  operator: 'array_contains',
                  value: values.join(', '),
                };
              }
            }
          }

          // Check if this is a single 'in' operation
          // array_contains uses: {"in": [value, {var: field}]} (value first)
          // regular 'in' uses: {"in": [{var: field}, value]} (var first)
          if (condition.in && Array.isArray(condition.in)) {
            const inOp = condition.in as [unknown, unknown];
            const first = inOp[0];
            const second = inOp[1];

            // Check if first is a value (string/number) and second is a var (array_contains pattern)
            const firstIsValue =
              typeof first === 'string' || typeof first === 'number' || typeof first === 'boolean';
            const secondIsVar = typeof second === 'object' && second !== null && 'var' in second;

            if (firstIsValue && secondIsVar) {
              // This is array_contains with single value
              const fieldName = (second as Record<string, unknown>).var as string;
              return {
                id: `condition_${Date.now()}_${index}`,
                field: fieldName,
                operator: 'array_contains',
                value: String(first),
              };
            }

            // Check if first is a var and second is a value (regular 'in' pattern)
            const firstIsVar = typeof first === 'object' && first !== null && 'var' in first;
            const secondIsValue =
              typeof second === 'string' || typeof second === 'number' || typeof second === 'boolean';

            if (firstIsVar && secondIsValue) {
              // This is regular 'in' operator
              const fieldName = (first as Record<string, unknown>).var as string;
              return {
                id: `condition_${Date.now()}_${index}`,
                field: fieldName,
                operator: 'in',
                value: String(second),
              };
            }
          }

          // Standard condition parsing
          const operator = Object.keys(condition)[0];
          const operatorValue = condition[operator];

          // Handle different operator structures
          if (Array.isArray(operatorValue) && operatorValue.length >= 2) {
            const [fieldVar, value] = operatorValue as [Record<string, unknown>, unknown];

            // Format value for display:
            // - If it's a string (from JSON), wrap in quotes to indicate it's a string literal
            //   This helps distinguish string "true" from boolean true
            // - If it's a boolean/number, convert to string (no quotes)
            let displayValue: string;
            if (typeof value === 'string') {
              // String values from JSON don't have quotes (quotes are JSON syntax)
              // Add quotes for display so user knows it's a string literal
              displayValue = `"${value}"`;
            } else {
              // Boolean, number, etc.: convert to string (no quotes)
              displayValue = String(value);
            }

            return {
              id: `condition_${Date.now()}_${index}`,
              field: ((fieldVar as Record<string, unknown>).var as string) || 'status',
              operator: operator,
              value: displayValue,
            };
          }

          // Fallback for unexpected structures
          return {
            id: `condition_${Date.now()}_${index}`,
            field: 'status',
            operator: operator,
            value: '',
          };
        };

        if (filter.and && Array.isArray(filter.and)) {
          setFilterLogic('and');
          const conditions = (filter.and as Array<Record<string, unknown>>).map(parseCondition);
          setFilterConditions(conditions);
        } else if (filter.or && Array.isArray(filter.or)) {
          setFilterLogic('or');
          const conditions = (filter.or as Array<Record<string, unknown>>).map(parseCondition);
          setFilterConditions(conditions);
        } else {
          setFilterConditions([]);
          setFilterLogic('and');
        }
      } else {
        setFilterConditions([]);
        setFilterLogic('and');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedSchema,
    mappings,
    selectedSchemaId,
    contactSourceType,
    additionalSourceIndex,
    initializeFieldMappings,
  ]);

  // Initialize the draft once per schema load. The key guard prevents a React
  // Query background refetch from wiping the user's in-progress edits.
  useEffect(() => {
    if (!selectedSchema || !mappings) return;
    const version = selectedSchema.versionNumber ?? selectedSchema.version_number;
    const loadKey = `${selectedSchema.id}:${version}`;
    if (initializedKeyRef.current === loadKey) return;
    initializedKeyRef.current = loadKey;
    setSelectedFieldName(null);
    initializeFromServer();
  }, [selectedSchema, mappings, initializeFromServer]);

  // Sync transaction filter edits into editedMappings so Save persists them.
  // Gated on filtersDirtyRef so initial hydration doesn't mark the draft dirty.
  useEffect(() => {
    if (selectedSchema?.category === 'transactions_schema' && filtersDirtyRef.current) {
      updateEditedMappings(fieldMappings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConditions, filterLogic]);

  // Warn on browser-level navigation (tab close / refresh) while the draft is dirty.
  // In-app schema switches are guarded separately via the discard confirm dialog.
  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const getAllMappingTypesForField = (fieldName: string, source: ContactSourceSchema): string[] => {
    // Check ALL field mappings and return all matches
    const mappings: string[] = [];
    if (source.email === fieldName) mappings.push('email');
    if (source.phone === fieldName) mappings.push('phone');
    if (source.userId === fieldName) mappings.push('userId');
    if (source.firstName === fieldName) mappings.push('firstName');
    if (source.lastName === fieldName) mappings.push('lastName');
    if (source.fullName === fieldName) mappings.push('fullName');
    if (source.location === fieldName) mappings.push('location');
    if (source.pointOfContactName === fieldName) mappings.push('pointOfContactName');
    if (source.pointOfContactEmail === fieldName) mappings.push('pointOfContactEmail');
    if (source.status?.field === fieldName) mappings.push('status');
    if (source.createdDate === fieldName) mappings.push('createdDate');
    if (source.emailOptOut === fieldName) mappings.push('emailOptOut');
    if (source.emailOptIn === fieldName) mappings.push('emailOptIn');
    if (source.emailOptOutTimestamp === fieldName) mappings.push('emailOptOutTimestamp');
    if (source.smsOptOut === fieldName) mappings.push('smsOptOut');
    if (source.smsOptIn === fieldName) mappings.push('smsOptIn');
    if (source.smsOptOutTimestamp === fieldName) mappings.push('smsOptOutTimestamp');
    if (source.hipaaConsent === fieldName) mappings.push('hipaaConsent');

    return mappings.length > 0 ? mappings : ['none'];
  };

  const isRequiredMapping = (mappingType: string, schemaCategory: string): boolean => {
    const requiredMappings: Record<string, string[]> = {
      contacts_schema: ['userId', 'createdDate'],
      locations_schema: ['id_field'],
      transactions_schema: ['id_field', 'userId'],
    };
    return requiredMappings[schemaCategory]?.includes(mappingType) || false;
  };

  const handleConflictResolution = async () => {
    if (!editedSchema || !selectedSchemaId || !mappings || !selectedSchema) return;

    setShowConflictDialog(false);

    // Store the ORIGINAL mappings before we clean them
    const originalMappings: SchemaMappingsType = {
      type: 'combined_schema',
      contactsSchema: mappings.contactsSchema,
      locationsSchema: mappings.locationsSchema,
      transactionsSchema: mappings.transactionsSchema,
      customSchema: mappings.customSchema,
    };

    // Create a copy of mappings with the conflicting fields removed
    const cleanedMappings: SchemaMappingsType = JSON.parse(JSON.stringify(originalMappings));

    const conflictFieldNames = conflictingFields.map(c => c.field);

    // Remove conflicting fields from the appropriate schema mappings
    if (selectedSchema.category === 'contacts_schema' && cleanedMappings.contactsSchema) {
      const cleanSource = (source: ContactSourceSchema) => {
        const cleaned: Partial<ContactSourceSchema> = JSON.parse(JSON.stringify(source));

        conflictFieldNames.forEach(field => {
          Object.keys(cleaned).forEach(keyStr => {
            const key = keyStr as keyof ContactSourceSchema;
            if (key === 'status') {
              if (cleaned.status?.field === field) {
                delete cleaned.status;
              }
            } else if (cleaned[key] === field) {
              delete cleaned[key];
            }
          });
        });

        return cleaned as ContactSourceSchema;
      };

      cleanedMappings.contactsSchema = {
        ...cleanedMappings.contactsSchema,
        primarySource: cleanSource(cleanedMappings.contactsSchema.primarySource),
        additionalSources: cleanedMappings.contactsSchema.additionalSources?.map(cleanSource),
      };
    } else if (selectedSchema.category === 'locations_schema' && cleanedMappings.locationsSchema) {
      cleanedMappings.locationsSchema = cleanedMappings.locationsSchema.map(loc => {
        if (loc.schemaId === selectedSchemaId) {
          const cleaned = { ...loc };
          conflictFieldNames.forEach(field => {
            Object.keys(cleaned).forEach(key => {
              if ((cleaned as Record<string, unknown>)[key] === field) {
                delete (cleaned as Record<string, unknown>)[key];
              }
            });
          });
          return cleaned;
        }
        return loc;
      });
    } else if (selectedSchema.category === 'transactions_schema' && cleanedMappings.transactionsSchema) {
      cleanedMappings.transactionsSchema = cleanedMappings.transactionsSchema.map(txn => {
        if (txn.schemaId === selectedSchemaId) {
          const cleaned = { ...txn };
          conflictFieldNames.forEach(field => {
            Object.keys(cleaned).forEach(key => {
              const value = (cleaned as Record<string, unknown>)[key];
              if (typeof value === 'string' && value === field) {
                delete (cleaned as Record<string, unknown>)[key];
              } else if (typeof value === 'object' && value !== null) {
                Object.keys(value).forEach(nestedKey => {
                  if ((value as Record<string, unknown>)[nestedKey] === field) {
                    delete (value as Record<string, unknown>)[nestedKey];
                  }
                });
              }
            });
          });
          return cleaned;
        }
        return txn;
      });
    }

    try {
      // Step 1: Update mappings to remove the conflicting fields
      await updateMappingsMutation.mutateAsync(cleanedMappings);

      // Step 2: Now update the schema
      const fieldsToExposeArray = buildFieldsToExpose(editedSchema.schema);
      const fieldsToExposeUpdate = buildFieldsToExposeUpdate({
        currentSchema: selectedSchema?.schema,
        nextSchema: editedSchema.schema,
        currentFieldsToExpose: selectedSchema?.fieldsToExpose ?? [],
        nextFieldsToExpose: fieldsToExposeArray,
      });
      const updateData: SchemaDefinitionUpdate = {
        description: editedSchema.description ?? undefined,
        schema: editedSchema.schema,
        category: editedSchema.category,
        externalIdField: editedSchema.externalIdField,
        fieldsToExpose: fieldsToExposeUpdate,
        exposeNameJsonPath: editedSchema.exposeNameJsonPath ?? undefined,
      };

      await updateSchemaMutation.mutateAsync({
        id: selectedSchemaId,
        data: updateData,
      });

      // Step 3: Restore the original mappings (or edited mappings if available)
      const finalMappings: SchemaMappingsType = editedMappings
        ? {
            type: 'combined_schema',
            // Start with original mappings
            contactsSchema: originalMappings.contactsSchema,
            locationsSchema: originalMappings.locationsSchema,
            transactionsSchema: originalMappings.transactionsSchema,
            customSchema: originalMappings.customSchema,
            // Override with edited mappings for this schema
            ...(selectedSchema.category === 'contacts_schema' && editedMappings.contactsSchema
              ? { contactsSchema: editedMappings.contactsSchema }
              : {}),
            ...(selectedSchema.category === 'locations_schema' && editedMappings.locationsSchema
              ? { locationsSchema: editedMappings.locationsSchema }
              : {}),
            ...(selectedSchema.category === 'transactions_schema' && editedMappings.transactionsSchema
              ? { transactionsSchema: editedMappings.transactionsSchema }
              : {}),
          }
        : originalMappings;

      await updateMappingsMutation.mutateAsync(finalMappings);

      setSchemaSuccessMessage('Schema and mappings updated successfully!');
      setConflictingFields([]);
    } catch (error) {
      setSchemaError('Failed to update schema. Please try again.');
    }
  };

  // Handle assigning a schema to contacts (primary or additional)
  // This sets up the intent to assign but doesn't save until user clicks Save
  const handleAssignSchema = (type: 'primary' | 'additional') => {
    if (!selectedSchema || !mappings) return;

    // Validate for additional assignment
    if (type === 'additional' && !mappings.contactsSchema?.primarySource?.schemaId) {
      setMappingError(
        'Cannot assign as Additional Contact without a Primary Contact. Please assign a Primary Contact first.',
      );
      setShowAssignSchemaDialog(false);
      return;
    }

    // Set pending assignment and enter edit mode
    setPendingAssignment(type);
    setContactSourceType(type);

    // Initialize editedMappings with a new contact source for this schema
    const newContactSource: ContactSourceSchema = {
      type: 'contacts_schema',
      schemaId: selectedSchema.id,
      userId: '',
      createdDate: '',
    };

    // Initialize editedMappings if needed
    if (!editedMappings || !editedMappings.contactsSchema) {
      const initialMappings: SchemaMappingsType = {
        type: 'combined_schema',
        contactsSchema: mappings.contactsSchema || {
          primarySource: newContactSource,
          additionalSources: [],
        },
      };

      // Set the source based on assignment type
      if (type === 'primary') {
        initialMappings.contactsSchema!.primarySource = newContactSource;
      } else {
        // Add to additional sources
        initialMappings.contactsSchema!.additionalSources = [
          ...(mappings.contactsSchema?.additionalSources || []),
          newContactSource,
        ];
        setAdditionalSourceIndex(mappings.contactsSchema?.additionalSources?.length || 0);
      }

      setEditedMappings(initialMappings);
    }

    // Close the dialog
    setShowAssignSchemaDialog(false);

    // The pending assignment is an unsaved change until the user clicks Save.
    markDirty();
  };

  // Handle swapping assignment (primary to additional or vice versa)
  // This sets up the intent to swap but doesn't save until user clicks Save
  const handleSwapAssignment = (newType: 'primary' | 'additional') => {
    if (!selectedSchema || !mappings) return;

    // Set pending assignment and update contact source type
    setPendingAssignment(newType);
    setContactSourceType(newType);

    // Always initialize/update editedMappings with the full contact schema to preserve mappings during swap
    if (mappings.contactsSchema) {
      // Find where this schema currently is
      const isCurrentlyPrimary = mappings.contactsSchema.primarySource?.schemaId === selectedSchema.id;
      const currentAdditionalIndex =
        mappings.contactsSchema.additionalSources?.findIndex(
          src => src.schemaId === selectedSchema.id,
        ) ?? -1;
      const isCurrentlyAdditional = currentAdditionalIndex !== -1;

      // Get the current source
      const currentSource = isCurrentlyPrimary
        ? mappings.contactsSchema.primarySource
        : isCurrentlyAdditional
          ? mappings.contactsSchema.additionalSources?.[currentAdditionalIndex]
          : null;

      if (currentSource) {
        // If editedMappings already exists, merge it; otherwise create new
        // This preserves any field-level edits the user may have made
        const existingContactSchema = editedMappings?.contactsSchema;

        // Get the source that might have field edits
        let updatedSource = currentSource;
        if (existingContactSchema) {
          // Check if there's an edited version of this specific source
          const editedSource = isCurrentlyPrimary
            ? existingContactSchema.primarySource
            : existingContactSchema.additionalSources?.[currentAdditionalIndex];

          // Use the edited source if it exists and matches our schema
          if (editedSource && editedSource.schemaId === selectedSchema.id) {
            updatedSource = editedSource;
          }
        }

        // Initialize editedMappings with the full contact schema, using the updated source
        const newContactsSchema = {
          primarySource: isCurrentlyPrimary ? updatedSource : mappings.contactsSchema.primarySource,
          additionalSources: isCurrentlyAdditional
            ? mappings.contactsSchema.additionalSources?.map((src, idx) =>
                idx === currentAdditionalIndex ? updatedSource : src,
              ) || []
            : mappings.contactsSchema.additionalSources || [],
        };

        setEditedMappings({
          type: 'combined_schema',
          contactsSchema: newContactsSchema,
        });
      }
    }

    // Close the warning dialog
    setShowSwapWarning(false);

    // The swap is an unsaved change until the user clicks Save.
    markDirty();
  };

  const handleSave = () => {
    if (!editedSchema || !selectedSchema || !selectedSchemaId) return;

    // Update schema with descriptions from fieldsToExpose if schema field doesn't have one
    const updatedSchema = { ...editedSchema.schema } as Record<string, unknown>;
    const schemaProperties = extractSchemaProperties(updatedSchema);

    Object.entries(fieldExposure).forEach(([fieldName, exposure]) => {
      if (exposure.description) {
        const field = schemaProperties.find(p => p.name === fieldName);
        // If schema field doesn't have a description but fieldsToExpose does, update the schema
        if (field && !field.description && exposure.description) {
          const properties = updatedSchema.properties as Record<string, unknown>;
          const fieldProperty = properties[fieldName] as Record<string, unknown>;
          if (fieldProperty) {
            fieldProperty.description = exposure.description;
          }
        }
      }
    });

    // Validate that all fields with exposure have a name set
    const fieldsWithoutNames: string[] = [];
    Object.entries(fieldExposure).forEach(([fieldName, exposure]) => {
      if (hasFieldExposure(exposure) && (!exposure.name || exposure.name.trim() === '')) {
        fieldsWithoutNames.push(fieldName);
      }
    });

    if (fieldsWithoutNames.length > 0) {
      setError(
        `The following fields have exposure enabled but are missing an Exposure Name: ${fieldsWithoutNames.join(', ')}. Please set an Exposure Name for each field with exposure enabled.`,
      );
      return;
    }

    // Build fieldsToExpose from fieldExposure state (using updated schema)
    const fieldsToExposeArray = buildFieldsToExpose(updatedSchema);
    const fieldsToExposeUpdate = buildFieldsToExposeUpdate({
      currentSchema: selectedSchema.schema,
      nextSchema: updatedSchema,
      currentFieldsToExpose: selectedSchema.fieldsToExpose ?? [],
      nextFieldsToExpose: fieldsToExposeArray,
    });

    // Update schema
    const updateData: SchemaDefinitionUpdate = {
      description: editedSchema.description ?? undefined,
      schema: updatedSchema,
      category: editedSchema.category,
      externalIdField: editedSchema.externalIdField,
      fieldsToExpose: fieldsToExposeUpdate,
      exposeNameJsonPath: editedSchema.exposeNameJsonPath ?? undefined,
    };

    // Update mappings if they were modified OR if there's a pending assignment - merge with existing mappings
    if ((editedMappings || pendingAssignment) && mappings && selectedSchema) {
      let mergedMappings: SchemaMappingsType = {
        type: 'combined_schema',
        // Preserve all existing mappings
        contactsSchema: mappings.contactsSchema,
        locationsSchema: mappings.locationsSchema,
        transactionsSchema: mappings.transactionsSchema,
        customSchema: mappings.customSchema,
      };

      // If there's a pending assignment for contacts schema, apply it now
      if (pendingAssignment && selectedSchema.category === 'contacts_schema') {
        // Check if this schema is currently assigned somewhere in the DB mappings
        const isCurrentlyPrimary =
          mappings.contactsSchema?.primarySource?.schemaId === selectedSchema.id;
        const currentAdditionalIndex =
          mappings.contactsSchema?.additionalSources?.findIndex(
            src => src.schemaId === selectedSchema.id,
          ) ?? -1;
        const isCurrentlyAdditional = currentAdditionalIndex !== -1;

        // Get the current contact source from DB mappings based on WHERE IT CURRENTLY IS
        // For unassigned schemas, get it from editedMappings (created by handleAssignSchema)
        const sourceToUse = editedMappings?.contactsSchema || mappings.contactsSchema;
        let editedContactSource = isCurrentlyPrimary
          ? sourceToUse?.primarySource
          : isCurrentlyAdditional
            ? sourceToUse?.additionalSources?.[currentAdditionalIndex]
            : null;

        // If not currently assigned, get the source from editedMappings based on pendingAssignment
        if (!editedContactSource && editedMappings?.contactsSchema) {
          editedContactSource =
            pendingAssignment === 'primary'
              ? editedMappings.contactsSchema.primarySource
              : editedMappings.contactsSchema.additionalSources?.[additionalSourceIndex];
        }

        if (editedContactSource) {
          if (pendingAssignment === 'primary') {
            // Moving to primary
            const existingPrimary = mappings.contactsSchema?.primarySource;
            let additionalSources = mappings.contactsSchema?.additionalSources || [];

            // If this schema is currently in additional, remove it from there
            if (isCurrentlyAdditional) {
              additionalSources = additionalSources.filter(src => src.schemaId !== selectedSchema.id);
            }

            // If there's an existing primary and it's not the current schema, move it to additional
            if (
              existingPrimary &&
              existingPrimary.schemaId &&
              existingPrimary.schemaId !== selectedSchema.id
            ) {
              additionalSources = [existingPrimary, ...additionalSources];
            }

            mergedMappings.contactsSchema = {
              primarySource: editedContactSource,
              additionalSources,
            };
          } else {
            // Moving to additional (this should not happen since UI doesn't allow demoting primary)
            // But we keep this for safety - just update the additional source if it exists
            let additionalSources = mappings.contactsSchema?.additionalSources || [];

            // If schema is already in additional sources, replace it with the edited version
            if (isCurrentlyAdditional) {
              additionalSources = additionalSources.map(src =>
                src.schemaId === selectedSchema.id ? editedContactSource : src,
              );
            } else {
              // Add as new additional source (for unassigned schemas)
              additionalSources = [...additionalSources, editedContactSource];
            }

            mergedMappings.contactsSchema = {
              primarySource: mappings.contactsSchema!.primarySource,
              additionalSources,
            };
          }
        }
      }

      // Override only the schema type we're editing (unless we just applied a pending assignment)
      if (
        selectedSchema.category === 'contacts_schema' &&
        editedMappings?.contactsSchema &&
        !pendingAssignment
      ) {
        mergedMappings.contactsSchema = editedMappings.contactsSchema;
      } else if (selectedSchema.category === 'locations_schema' && editedMappings?.locationsSchema) {
        // For locations, merge the edited schema with existing schemas (preserving others)
        const existingLocations = mappings.locationsSchema || [];
        const editedLocation = editedMappings.locationsSchema.find(
          loc => loc.schemaId === selectedSchemaId,
        );

        if (editedLocation) {
          // Check if this schema already exists in the array
          const existingIndex = existingLocations.findIndex(loc => loc.schemaId === selectedSchemaId);
          if (existingIndex !== -1) {
            // Replace existing entry
            mergedMappings.locationsSchema = [
              ...existingLocations.slice(0, existingIndex),
              editedLocation,
              ...existingLocations.slice(existingIndex + 1),
            ];
          } else {
            // Add new entry
            mergedMappings.locationsSchema = [...existingLocations, editedLocation];
          }
        } else {
          // No mappings for this schema, keep existing
          mergedMappings.locationsSchema = existingLocations;
        }
      } else if (
        selectedSchema.category === 'transactions_schema' &&
        editedMappings?.transactionsSchema
      ) {
        // For transactions, merge the edited schema with existing schemas (preserving others)
        const existingTransactions = mappings.transactionsSchema || [];
        const editedTransaction = editedMappings.transactionsSchema.find(
          txn => txn.schemaId === selectedSchemaId,
        );

        if (editedTransaction) {
          // Check if this schema already exists in the array
          const existingIndex = existingTransactions.findIndex(txn => txn.schemaId === selectedSchemaId);
          if (existingIndex !== -1) {
            // Replace existing entry
            mergedMappings.transactionsSchema = [
              ...existingTransactions.slice(0, existingIndex),
              editedTransaction,
              ...existingTransactions.slice(existingIndex + 1),
            ];
          } else {
            // Add new entry
            mergedMappings.transactionsSchema = [...existingTransactions, editedTransaction];
          }
        } else {
          // No mappings for this schema, keep existing
          mergedMappings.transactionsSchema = existingTransactions;
        }
      } else if (selectedSchema.category === 'custom_schema' && editedMappings?.customSchema) {
        mergedMappings.customSchema = editedMappings.customSchema;
      }

      // Detect if a required mapped field is being renamed or its type is changed in a way that affects mapping
      const originalSchemaProperties = extractSchemaProperties(selectedSchema.schema);
      const changedFields = [];
      if (editableFields.length === originalSchemaProperties.length) {
        for (const [i, originalProp] of originalSchemaProperties.entries()) {
          const editedField = editableFields[i];
          if (editedField) {
            const isRenamed = originalProp.name !== editedField.name;
            const isTypeChangedToReferenceOrFromReference =
              originalProp.type !== editedField.type &&
              (originalProp.type === 'reference' || editedField.type === 'reference');

            if (isRenamed || isTypeChangedToReferenceOrFromReference) {
              changedFields.push({
                oldName: originalProp.name,
                newName: editedField.name,
              });
            }
          }
        }
      }

      if (changedFields.length > 0 && mappings && selectedSchema) {
        const getMappingInfoForField = (
          fieldName: string,
        ): { mappingKey: string; mapping: Record<string, unknown> } | null => {
          const { category } = selectedSchema;
          if (category === 'contacts_schema' && mappings.contactsSchema) {
            const sources = [
              mappings.contactsSchema.primarySource,
              ...(mappings.contactsSchema.additionalSources || []),
            ];
            for (const source of sources) {
              if (source.schemaId === selectedSchema.id) {
                for (const key in source) {
                  if ((source as Record<string, unknown>)[key] === fieldName) {
                    return { mappingKey: key, mapping: source };
                  }
                }
              }
            }
          } else if (category === 'locations_schema' && mappings.locationsSchema) {
            const mapping = mappings.locationsSchema.find(m => m.schemaId === selectedSchemaId);
            if (mapping) {
              for (const key in mapping) {
                if ((mapping as Record<string, unknown>)[key] === fieldName) {
                  return { mappingKey: key, mapping };
                }
              }
            }
          } else if (category === 'transactions_schema' && mappings.transactionsSchema) {
            const mapping = mappings.transactionsSchema.find(m => m.schemaId === selectedSchemaId);
            if (mapping) {
              for (const key in mapping) {
                if ((mapping as Record<string, unknown>)[key] === fieldName) {
                  return { mappingKey: key, mapping };
                }
              }
            }
          }
          return null;
        };

        const renamedRequiredFields = changedFields
          .map(({ oldName, newName }) => {
            const mappingInfo = getMappingInfoForField(oldName);
            return {
              oldName,
              newName,
              mappingKey: mappingInfo?.mappingKey,
            };
          })
          .filter(f => f.mappingKey && isRequiredMapping(f.mappingKey, selectedSchema.category));

        if (renamedRequiredFields.length > 0) {
          // For simplicity, handle the first renamed required field.
          // The UI makes it unlikely for users to rename multiple required fields simultaneously.
          handleSaveWithRequiredFieldRename(
            renamedRequiredFields[0] as {
              oldName: string;
              newName: string;
              mappingKey: string;
            },
            updateData,
            mergedMappings,
          );
          return;
        }
      }

      // Update schema first, then mappings on success if there are mapping changes
      if ((editedMappings || pendingAssignment) && mappings && selectedSchema) {
        let mergedMappings: SchemaMappingsType = {
          type: 'combined_schema',
          // Preserve all existing mappings
          contactsSchema: mappings.contactsSchema,
          locationsSchema: mappings.locationsSchema,
          transactionsSchema: mappings.transactionsSchema,
          customSchema: mappings.customSchema,
        };

        // If there's a pending assignment for contacts schema, apply it now
        if (pendingAssignment && selectedSchema.category === 'contacts_schema') {
          // Check if this schema is currently assigned somewhere in the DB mappings
          const isCurrentlyPrimary =
            mappings.contactsSchema?.primarySource?.schemaId === selectedSchema.id;
          const currentAdditionalIndex =
            mappings.contactsSchema?.additionalSources?.findIndex(
              src => src.schemaId === selectedSchema.id,
            ) ?? -1;
          const isCurrentlyAdditional = currentAdditionalIndex !== -1;

          // Get the current contact source from DB mappings based on WHERE IT CURRENTLY IS
          // For unassigned schemas, get it from editedMappings (created by handleAssignSchema)
          const sourceToUse = editedMappings?.contactsSchema || mappings.contactsSchema;
          let editedContactSource = isCurrentlyPrimary
            ? sourceToUse?.primarySource
            : isCurrentlyAdditional
              ? sourceToUse?.additionalSources?.[currentAdditionalIndex]
              : null;

          // If not currently assigned, get the source from editedMappings based on pendingAssignment
          if (!editedContactSource && editedMappings?.contactsSchema) {
            editedContactSource =
              pendingAssignment === 'primary'
                ? editedMappings.contactsSchema.primarySource
                : editedMappings.contactsSchema.additionalSources?.[additionalSourceIndex];
          }

          if (editedContactSource) {
            if (pendingAssignment === 'primary') {
              // Moving to primary
              const existingPrimary = mappings.contactsSchema?.primarySource;
              let additionalSources = mappings.contactsSchema?.additionalSources || [];

              // If this schema is currently in additional, remove it from there
              if (isCurrentlyAdditional) {
                additionalSources = additionalSources.filter(src => src.schemaId !== selectedSchema.id);
              }

              // If there's an existing primary and it's not the current schema, move it to additional
              if (
                existingPrimary &&
                existingPrimary.schemaId &&
                existingPrimary.schemaId !== selectedSchema.id
              ) {
                additionalSources = [existingPrimary, ...additionalSources];
              }

              mergedMappings.contactsSchema = {
                primarySource: editedContactSource,
                additionalSources,
              };
            } else {
              // Moving to additional (this should not happen since UI doesn't allow demoting primary)
              // But we keep this for safety - just update the additional source if it exists
              let additionalSources = mappings.contactsSchema?.additionalSources || [];

              // If schema is already in additional sources, replace it with the edited version
              if (isCurrentlyAdditional) {
                additionalSources = additionalSources.map(src =>
                  src.schemaId === selectedSchema.id ? editedContactSource : src,
                );
              } else {
                // Add as new additional source (for unassigned schemas)
                additionalSources = [...additionalSources, editedContactSource];
              }

              mergedMappings.contactsSchema = {
                primarySource: mappings.contactsSchema!.primarySource,
                additionalSources,
              };
            }
          }
        }

        // Override only the schema type we're editing (unless we just applied a pending assignment)
        if (
          selectedSchema.category === 'contacts_schema' &&
          editedMappings?.contactsSchema &&
          !pendingAssignment
        ) {
          mergedMappings.contactsSchema = editedMappings.contactsSchema;
        } else if (selectedSchema.category === 'locations_schema' && editedMappings?.locationsSchema) {
          // For locations, merge the edited schema with existing schemas (preserving others)
          const existingLocations = mappings.locationsSchema || [];
          const editedLocation = editedMappings.locationsSchema.find(
            loc => loc.schemaId === selectedSchemaId,
          );

          if (editedLocation) {
            // Check if this schema already exists in the array
            const existingIndex = existingLocations.findIndex(loc => loc.schemaId === selectedSchemaId);
            if (existingIndex !== -1) {
              // Replace existing entry
              mergedMappings.locationsSchema = [
                ...existingLocations.slice(0, existingIndex),
                editedLocation,
                ...existingLocations.slice(existingIndex + 1),
              ];
            } else {
              // Add new entry
              mergedMappings.locationsSchema = [...existingLocations, editedLocation];
            }
          } else {
            // No mappings for this schema, keep existing
            mergedMappings.locationsSchema = existingLocations;
          }
        } else if (
          selectedSchema.category === 'transactions_schema' &&
          editedMappings?.transactionsSchema
        ) {
          // For transactions, merge the edited schema with existing schemas (preserving others)
          const existingTransactions = mappings.transactionsSchema || [];
          const editedTransaction = editedMappings.transactionsSchema.find(
            txn => txn.schemaId === selectedSchemaId,
          );

          if (editedTransaction) {
            // Check if this schema already exists in the array
            const existingIndex = existingTransactions.findIndex(
              txn => txn.schemaId === selectedSchemaId,
            );
            if (existingIndex !== -1) {
              // Replace existing entry
              mergedMappings.transactionsSchema = [
                ...existingTransactions.slice(0, existingIndex),
                editedTransaction,
                ...existingTransactions.slice(existingIndex + 1),
              ];
            } else {
              // Add new entry
              mergedMappings.transactionsSchema = [...existingTransactions, editedTransaction];
            }
          } else {
            // No mappings for this schema, keep existing
            mergedMappings.transactionsSchema = existingTransactions;
          }
        } else if (selectedSchema.category === 'custom_schema' && editedMappings?.customSchema) {
          mergedMappings.customSchema = editedMappings.customSchema;
        }

        // Update schema first, then mappings on success
        updateSchemaMutation.mutate(
          { id: selectedSchemaId, data: updateData },
          {
            onSuccess: async () => {
              // Invalidate schema queries to ensure fresh data before mapping validation
              await queryClient.invalidateQueries({
                queryKey: schemaDefinitionKeys.detail(selectedSchemaId),
              });
              await queryClient.invalidateQueries({
                queryKey: schemaDefinitionKeys.list(),
              });
              await queryClient.invalidateQueries({
                queryKey: ['availableSegmentFieldsWithTypes'],
              });

              // Small delay to ensure database write is complete
              await new Promise(resolve => setTimeout(resolve, 100));

              // Only update mappings after schema update succeeds and cache is invalidated
              updateMappingsMutation.mutate(mergedMappings);
            },
            onError: () => {
              // Error handling is done in the mutation's global onError
            },
          },
        );
      } else {
        // No mappings to update, just update schema
        updateSchemaMutation.mutate({ id: selectedSchemaId, data: updateData });
      }
    } else {
      // No mappings to update, just update schema
      updateSchemaMutation.mutate({ id: selectedSchemaId, data: updateData });
    }
  };

  const replaceMappingsForField = (
    mappingsToUpdate: SchemaMappingsType,
    schemaId: string,
    category: string,
    oldFieldName: string,
    newFieldName: string,
  ): SchemaMappingsType => {
    const updatedMappings = JSON.parse(JSON.stringify(mappingsToUpdate));

    const replaceInObject = (obj: Record<string, unknown>) => {
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          if (typeof obj[key] === 'string' && obj[key] === oldFieldName) {
            obj[key] = newFieldName;
          } else if (typeof obj[key] === 'object' && obj[key] !== null) {
            replaceInObject(obj[key] as Record<string, unknown>);
          }
        }
      }
    };

    if (category === 'contacts_schema' && updatedMappings.contactsSchema) {
      const { primarySource, additionalSources } = updatedMappings.contactsSchema;
      if (primarySource?.schemaId === schemaId) {
        replaceInObject(primarySource);
      }
      additionalSources?.forEach((source: Record<string, unknown>) => {
        if (source.schemaId === schemaId) {
          replaceInObject(source);
        }
      });
    } else if (category === 'locations_schema' && updatedMappings.locationsSchema) {
      updatedMappings.locationsSchema.forEach((mapping: Record<string, unknown>) => {
        if (mapping.schemaId === schemaId) {
          replaceInObject(mapping);
        }
      });
    } else if (category === 'transactions_schema' && updatedMappings.transactionsSchema) {
      updatedMappings.transactionsSchema.forEach((mapping: Record<string, unknown>) => {
        if (mapping.schemaId === schemaId) {
          replaceInObject(mapping);
        }
      });
    }

    return updatedMappings;
  };

  const handleSaveWithRequiredFieldRename = async (
    renamedField: { oldName: string; newName: string; mappingKey: string },
    finalSchemaUpdateData: SchemaDefinitionUpdate,
    finalMappings: SchemaMappingsType,
  ) => {
    if (!selectedSchema || !mappings || !selectedSchemaId) return;

    const { oldName } = renamedField;
    const dummyFieldName = `__temp_field_for_rename_${Date.now()}`;

    try {
      setSchemaSuccessMessage('Performing complex update...');

      // Step 1: Add a dummy field to the original schema
      const schemaWithDummy = JSON.parse(JSON.stringify(selectedSchema.schema));
      schemaWithDummy.properties[dummyFieldName] = {
        type: 'string',
        description: 'Temporary field for safe rename of required field',
      };
      const initialSchemaUpdateData = {
        ...finalSchemaUpdateData,
        schema: schemaWithDummy,
      };
      await updateSchemaMutation.mutateAsync({
        id: selectedSchemaId,
        data: initialSchemaUpdateData,
      });

      // Step 2: Update mapping to point to the dummy field
      const tempMappings = replaceMappingsForField(
        mappings,
        selectedSchemaId,
        selectedSchema.category,
        oldName,
        dummyFieldName,
      );
      await updateMappingsMutation.mutateAsync(tempMappings);

      // Step 3: Update schema with the actual rename (and keep dummy field)
      const finalSchemaWithDummy = JSON.parse(JSON.stringify(finalSchemaUpdateData.schema));
      finalSchemaWithDummy.properties[dummyFieldName] = {
        type: 'string',
        description: 'Temporary field for safe rename of required field',
      };
      const finalSchemaUpdateWithDummy = {
        ...finalSchemaUpdateData,
        schema: finalSchemaWithDummy,
      };
      await updateSchemaMutation.mutateAsync({
        id: selectedSchemaId,
        data: finalSchemaUpdateWithDummy,
      });

      // Step 4: Update mappings to point to the new field name (final state)
      await updateMappingsMutation.mutateAsync(finalMappings);

      // Step 5: Remove the dummy field by saving the final schema state
      await updateSchemaMutation.mutateAsync({
        id: selectedSchemaId,
        data: finalSchemaUpdateData,
      });

      setSchemaSuccessMessage('Schema and mappings updated successfully!');
      setHasUnsavedChanges(false);
    } catch (err: unknown) {
      const errorMessage = parseApiError(err as ApiError);
      setSchemaError(`Complex update failed: ${errorMessage.message}. Please try again.`);
      setErrorDetails(errorMessage.details);
      setSchemaSuccessMessage('');
    }
  };

  const handleAddMapping = (fieldName: string, mappingType: string) => {
    markDirty();
    setFieldMappings(prev => {
      const updated = prev.map(mapping => {
        if (mapping.fieldName === fieldName) {
          // Add the new mapping type if it's not already there
          const newTypes = mapping.mappingTypes.filter(t => t !== 'none');
          if (!newTypes.includes(mappingType)) {
            newTypes.push(mappingType);
          }
          return {
            ...mapping,
            mappingTypes: newTypes,
          };
        }
        // Remove this mapping type from other fields (mappings are unique across fields)
        const filteredTypes = mapping.mappingTypes.filter(t => t !== mappingType);
        return {
          ...mapping,
          mappingTypes: filteredTypes.length > 0 ? filteredTypes : ['none'],
        };
      });

      // Update editedMappings with the new mappings
      updateEditedMappings(updated);

      return updated;
    });

    // Update externalIdField on schema definition if mapping is id_field or userId
    if (editedSchema && (mappingType === 'id_field' || mappingType === 'userId')) {
      setEditedSchema({
        ...editedSchema,
        externalIdField: fieldName,
      });
    }
  };

  const handleRemoveMapping = (fieldName: string, mappingType: string) => {
    markDirty();
    let updatedMappings: FieldMapping[] = [];

    setFieldMappings(prev => {
      const updated = prev.map(mapping => {
        if (mapping.fieldName === fieldName) {
          const newTypes = mapping.mappingTypes.filter(t => t !== mappingType);
          return {
            ...mapping,
            mappingTypes: newTypes.length > 0 ? newTypes : ['none'],
          };
        }
        return mapping;
      });

      updatedMappings = updated;

      // Update editedMappings with the new mappings
      updateEditedMappings(updated);

      return updated;
    });

    // Clear externalIdField if we removed id_field or userId mapping
    // Use the updated mappings, not the old state
    if (
      editedSchema &&
      (mappingType === 'id_field' || mappingType === 'userId') &&
      editedSchema.externalIdField === fieldName
    ) {
      const fieldMapping = updatedMappings.find(m => m.fieldName === fieldName);
      const stillHasIdMapping = fieldMapping?.mappingTypes.some(t => t === 'id_field' || t === 'userId');
      if (!stillHasIdMapping) {
        setEditedSchema({
          ...editedSchema,
          externalIdField: '',
        });
      }
    }
  };

  const updateEditedMappings = (updatedMappings: FieldMapping[]) => {
    if (!selectedSchema) return;

    // Initialize editedMappings if needed
    if (!editedMappings) {
      // Start with only the mappings for the current schema category
      const initialMappings: SchemaMappingsType = {
        type: 'combined_schema' as const,
      };

      // Only include the schema type we're editing - copy existing data if available
      if (selectedSchema.category === 'contacts_schema') {
        initialMappings.contactsSchema = mappings?.contactsSchema || {
          primarySource: {
            type: 'contacts_schema',
            schemaId: selectedSchemaId,
            userId: '',
            createdDate: '',
          },
        };
      } else if (selectedSchema.category === 'locations_schema') {
        initialMappings.locationsSchema = mappings?.locationsSchema || [];
      } else if (selectedSchema.category === 'transactions_schema') {
        initialMappings.transactionsSchema = mappings?.transactionsSchema || [];
      } else if (selectedSchema.category === 'custom_schema') {
        initialMappings.customSchema = mappings?.customSchema || [];
      }

      setEditedMappings(initialMappings);
      return; // Will be called again on next render with initialized editedMappings
    }

    // Update based on schema category
    if (selectedSchema.category === 'contacts_schema' && editedMappings.contactsSchema) {
      // Determine if we're updating primary or additional source
      const isPrimarySource =
        contactSourceType === 'primary' &&
        editedMappings.contactsSchema.primarySource.schemaId === selectedSchemaId;
      const isAdditionalSource =
        contactSourceType === 'additional' && editedMappings.contactsSchema.additionalSources;

      if (isPrimarySource) {
        // Create a new object with only type and schemaId, then add mapped fields
        const rebuiltSource: Partial<ContactSourceSchema> &
          Record<string, unknown> & {
            type: string;
            schemaId: string;
          } = {
          type: editedMappings.contactsSchema.primarySource.type,
          schemaId: editedMappings.contactsSchema.primarySource.schemaId,
        };
        const updatedSource = preserveMappingMetadata(
          editedMappings.contactsSchema.primarySource,
          rebuiltSource,
          SCHEMA_MAPPING_METADATA_KEYS,
        );

        // Get the current schema properties to validate field existence
        const schemaProperties = selectedSchema ? extractSchemaProperties(selectedSchema.schema) : [];
        const validFieldNames = new Set(schemaProperties.map(p => p.name));

        // Add only fields that are currently mapped with non-empty values AND exist in schema
        updatedMappings.forEach(mapping => {
          if (
            mapping.fieldName &&
            mapping.fieldName.trim().length > 0 &&
            validFieldNames.has(mapping.fieldName)
          ) {
            // Loop through all mapping types for this field
            mapping.mappingTypes.forEach(mappingType => {
              if (mappingType !== 'none') {
                // Special handling for status field - preserve the object structure
                if (mappingType === 'status') {
                  // If status was previously set, preserve it
                  const existingStatus = editedMappings.contactsSchema?.primarySource.status;
                  if (existingStatus && typeof existingStatus === 'object') {
                    updatedSource.status = existingStatus;
                  }
                } else {
                  updatedSource[mappingType] = mapping.fieldName;
                }
              }
            });
          }
        });

        setEditedMappings({
          ...editedMappings,
          contactsSchema: {
            ...editedMappings.contactsSchema,
            primarySource: updatedSource as ContactSourceSchema,
          },
        });
      } else if (isAdditionalSource) {
        // Update additional source at the specified index
        const updatedAdditionalSources = editedMappings.contactsSchema.additionalSources!.map(
          (src, idx) => {
            if (idx === additionalSourceIndex && src.schemaId === selectedSchemaId) {
              // Create a new object with only type and schemaId, then add mapped fields
              const rebuiltSource: Partial<ContactSourceSchema> &
                Record<string, unknown> & {
                  type: string;
                  schemaId: string;
                } = {
                type: src.type,
                schemaId: src.schemaId,
              };
              const updatedSource = preserveMappingMetadata(
                src,
                rebuiltSource,
                SCHEMA_MAPPING_METADATA_KEYS,
              );

              // Get the current schema properties to validate field existence
              const schemaProperties = selectedSchema
                ? extractSchemaProperties(selectedSchema.schema)
                : [];
              const validFieldNames = new Set(schemaProperties.map(p => p.name));

              // Add only fields that are currently mapped with non-empty values AND exist in schema
              updatedMappings.forEach(mapping => {
                if (
                  mapping.fieldName &&
                  mapping.fieldName.trim().length > 0 &&
                  validFieldNames.has(mapping.fieldName)
                ) {
                  // Loop through all mapping types for this field
                  mapping.mappingTypes.forEach(mappingType => {
                    if (mappingType !== 'none') {
                      // Special handling for status field - preserve the object structure
                      if (mappingType === 'status') {
                        const existingStatus = src.status;
                        if (existingStatus && typeof existingStatus === 'object') {
                          updatedSource.status = existingStatus;
                        }
                      } else {
                        updatedSource[mappingType] = mapping.fieldName;
                      }
                    }
                  });
                }
              });

              return updatedSource as ContactSourceSchema;
            }
            return src;
          },
        );

        setEditedMappings({
          ...editedMappings,
          contactsSchema: {
            ...editedMappings.contactsSchema,
            additionalSources: updatedAdditionalSources,
          },
        });
      }
    } else if (selectedSchema.category === 'locations_schema') {
      // Update locations schema mapping
      const existingLocations = editedMappings.locationsSchema || [];
      const existingIndex = existingLocations.findIndex(loc => loc.schemaId === selectedSchemaId);
      const existingLocation = existingIndex !== -1 ? existingLocations[existingIndex] : null;

      // Create a new object with only type and schemaId, then add mapped fields
      const rebuiltSource: Record<string, string> & {
        type: 'locations_schema';
        schemaId: string;
      } = {
        type: 'locations_schema' as const,
        schemaId: selectedSchemaId,
      };
      const updatedSource = preserveMappingMetadata(
        existingLocation,
        rebuiltSource,
        SCHEMA_MAPPING_METADATA_KEYS,
      );

      // Get the current schema properties to validate field existence
      const schemaProperties = selectedSchema ? extractSchemaProperties(selectedSchema.schema) : [];
      const validFieldNames = new Set(schemaProperties.map(p => p.name));

      // Add only fields that are currently mapped with non-empty values AND exist in schema
      updatedMappings.forEach(mapping => {
        if (
          mapping.fieldName &&
          mapping.fieldName.trim().length > 0 &&
          validFieldNames.has(mapping.fieldName)
        ) {
          // Loop through all mapping types for this field
          mapping.mappingTypes.forEach(mappingType => {
            if (mappingType !== 'none') {
              updatedSource[mappingType] = mapping.fieldName;
            }
          });
        }
      });

      // Ensure id_field is present (required field)
      if (!updatedSource.id_field) {
        // Find the external ID field mapping
        const idFieldMapping = updatedMappings.find(m => m.mappingTypes.includes('id_field'));
        if (
          idFieldMapping &&
          idFieldMapping.fieldName &&
          validFieldNames.has(idFieldMapping.fieldName)
        ) {
          updatedSource.id_field = idFieldMapping.fieldName;
        }
      }

      let updatedLocations: typeof existingLocations;
      if (existingIndex !== -1) {
        // Replace existing entry
        updatedLocations = [
          ...existingLocations.slice(0, existingIndex),
          updatedSource as (typeof existingLocations)[0],
          ...existingLocations.slice(existingIndex + 1),
        ];
      } else {
        // Add new entry
        updatedLocations = [...existingLocations, updatedSource as (typeof existingLocations)[0]];
      }

      setEditedMappings({
        ...editedMappings,
        locationsSchema: updatedLocations,
      });
    } else if (selectedSchema.category === 'transactions_schema') {
      // Update transactions schema mapping
      const existingTransactions = editedMappings.transactionsSchema || [];
      const existingIndex = existingTransactions.findIndex(txn => txn.schemaId === selectedSchemaId);
      let existingTransaction = existingIndex !== -1 ? existingTransactions[existingIndex] : null;

      // If not found in editedMappings, try to get from database mappings
      if (!existingTransaction && mappings?.transactionsSchema) {
        existingTransaction =
          mappings.transactionsSchema.find(txn => txn.schemaId === selectedSchemaId) || null;
      }

      // Create a new object with only type, schemaId, and eventType, then add mapped fields
      const rebuiltSource: Record<string, unknown> & {
        type: 'transactions_schema';
        schemaId: string;
        eventType: string;
        dates: Record<string, string>;
        filter?: Record<string, unknown>;
      } = {
        type: 'transactions_schema' as const,
        schemaId: selectedSchemaId,
        eventType: existingTransaction?.eventType || '',
        dates: existingTransaction?.dates || {}, // dates is required, preserve existing or initialize as empty object
      };
      const updatedSource = preserveMappingMetadata(
        existingTransaction,
        rebuiltSource,
        SCHEMA_MAPPING_METADATA_KEYS,
      );

      // Add filter conditions if they exist
      if (filterConditions.length > 0) {
        // Helper to convert string values to proper types (boolean, number, or keep as string)
        // If value is wrapped in quotes (e.g., "true"), treat as string literal
        // Otherwise, auto-convert "true"/"false" to booleans and numeric strings to numbers
        const convertValue = (val: string): string | number | boolean => {
          if (!val || typeof val !== 'string') {
            return val;
          }

          const trimmed = val.trim();

          // Check if value is wrapped in quotes (string literal)
          // Must be at least 2 characters (one quote on each side)
          if (trimmed.length >= 2) {
            if (
              (trimmed[0] === '"' && trimmed[trimmed.length - 1] === '"') ||
              (trimmed[0] === "'" && trimmed[trimmed.length - 1] === "'")
            ) {
              // Remove quotes and return as string
              const unquoted = trimmed.slice(1, -1);
              return unquoted;
            }
          }

          // Auto-convert boolean strings (only if not quoted)
          if (trimmed.toLowerCase() === 'true') {
            return true;
          }
          if (trimmed.toLowerCase() === 'false') {
            return false;
          }

          // Auto-convert numeric strings
          if (trimmed !== '' && !isNaN(Number(trimmed)) && isFinite(Number(trimmed))) {
            return Number(trimmed);
          }

          // Keep as string
          return val;
        };

        const filter = {
          [filterLogic]: filterConditions.map(condition => {
            // Handle array_contains operator specially
            if (condition.operator === 'array_contains') {
              // Parse comma-separated values and convert types
              const values = condition.value
                .split(',')
                .map(v => v.trim())
                .filter(v => v.length > 0)
                .map(convertValue);

              // If no values, return a condition that never matches
              if (values.length === 0) {
                return { '==': [false, true] }; // Always false
              }

              // If single value, use a single in operation
              if (values.length === 1) {
                return {
                  in: [values[0], { var: condition.field }],
                };
              }

              // Multiple values: use or with multiple in operations
              return {
                or: values.map(value => ({
                  in: [value, { var: condition.field }],
                })),
              };
            }

            // Standard operators
            return {
              [condition.operator]: [{ var: condition.field }, convertValue(condition.value)],
            };
          }),
        };
        updatedSource.filter = filter;
      }

      // Get the current schema properties to validate field existence
      const schemaProperties = selectedSchema ? extractSchemaProperties(selectedSchema.schema) : [];
      const validFieldNames = new Set(schemaProperties.map(p => p.name));

      // If updatedMappings is empty, preserve all existing field mappings from existingTransaction
      if (updatedMappings.length === 0 && existingTransaction) {
        // Copy all existing mappings from the existing transaction
        Object.keys(existingTransaction).forEach(key => {
          if (
            key !== 'type' &&
            key !== 'schemaId' &&
            key !== 'eventType' &&
            key !== 'filter' &&
            key !== 'urlPattern' &&
            key !== 'dates'
          ) {
            const value = (existingTransaction as Record<string, unknown>)[key];
            if (typeof value === 'string') {
              updatedSource[key] = value;
            } else if (typeof value === 'object' && value !== null) {
              // Handle nested objects like amount
              updatedSource[key] = value;
            }
          }
        });
        // Preserve dates object
        if (existingTransaction.dates) {
          updatedSource.dates = existingTransaction.dates;
        }
      } else {
        // Set the new mapping from updatedMappings
        updatedMappings.forEach(mapping => {
          if (
            mapping.fieldName &&
            mapping.fieldName.trim().length > 0 &&
            validFieldNames.has(mapping.fieldName)
          ) {
            // Loop through all mapping types for this field
            mapping.mappingTypes.forEach(mappingType => {
              if (mappingType !== 'none') {
                // Special handling for amount - it's an object
                if (mappingType === 'amount') {
                  // Preserve existing currency and currencyField when updating amount
                  const existingAmount = (existingTransaction?.amount || {}) as TransactionAmount;
                  updatedSource.amount = {
                    field: mapping.fieldName,
                    currency: existingAmount.currency,
                    currencyField: existingAmount.currencyField,
                  };
                } else if (mappingType.startsWith('dates.')) {
                  // Special handling for dates - it's an object with optional string fields
                  const dateField = mappingType.replace('dates.', '');
                  if (!updatedSource.dates) {
                    updatedSource.dates = {};
                  }
                  updatedSource.dates[dateField] = mapping.fieldName;
                } else {
                  updatedSource[mappingType] = mapping.fieldName;
                }
              }
            });
          }
        });
      }

      let updatedTransactions: typeof existingTransactions;
      if (existingIndex !== -1) {
        // Replace existing entry
        updatedTransactions = [
          ...existingTransactions.slice(0, existingIndex),
          updatedSource as (typeof existingTransactions)[0],
          ...existingTransactions.slice(existingIndex + 1),
        ];
      } else {
        // Add new entry
        updatedTransactions = [
          ...existingTransactions,
          updatedSource as (typeof existingTransactions)[0],
        ];
      }

      setEditedMappings({
        ...editedMappings,
        transactionsSchema: updatedTransactions,
      });
    }
  };

  // Adds a fresh blank attribute and opens the editor panel on it. The user gives
  // it a real name and type in the panel (handleFieldChange handles the rename).
  const handleAddAttribute = () => {
    if (!editedSchema) return;

    const existingNames = new Set(extractSchemaProperties(editedSchema.schema).map(p => p.name));
    let candidate = 'newAttribute';
    let suffix = 2;
    while (existingNames.has(candidate)) {
      candidate = `newAttribute${suffix}`;
      suffix += 1;
    }
    const fieldName = candidate;

    const updatedSchema = { ...editedSchema.schema } as Record<string, unknown>;
    const properties = { ...(updatedSchema.properties as Record<string, unknown>) };
    updatedSchema.properties = properties;
    properties[fieldName] = { type: 'string', description: '' };

    setEditedSchema({ ...editedSchema, schema: updatedSchema });
    setEditableFields(prev => [
      ...prev,
      {
        name: fieldName,
        type: 'string',
        format: undefined,
        enum: undefined,
        required: false,
        description: '',
      },
    ]);
    setFieldMappings(prev => [...prev, { fieldName, mappingTypes: ['none'], jsonPath: fieldName }]);
    markDirty();
    setError('');
    setSelectedFieldName(fieldName);
  };

  const handleConfirmFieldRemoval = () => {
    if (!editedSchema || !fieldPendingRemoval) return;

    const fieldName = fieldPendingRemoval.name;
    const externalIdField = editedSchema.externalIdField || selectedSchema?.externalIdField;

    if (fieldName === externalIdField) {
      setError('The external ID field cannot be removed.');
      setErrorDetails([]);
      setFieldPendingRemoval(null);
      return;
    }

    const updatedSchema = removeSchemaField(editedSchema.schema, fieldName);

    setEditedSchema({
      ...editedSchema,
      schema: updatedSchema,
    });
    setEditableFields(prev => prev.filter(field => field.name !== fieldName));
    setFieldMappings(prev => prev.filter(mapping => mapping.fieldName !== fieldName));
    setFieldExposure(prev => removeFieldExposure(prev, fieldName));
    setFilterConditions(prev => prev.filter(condition => condition.field !== fieldName));
    markDirty();
    setError('');
    setErrorDetails([]);
    setFieldPendingRemoval(null);
    // Close the panel if it was showing the field we just removed.
    setSelectedFieldName(current => (current === fieldName ? null : current));
  };

  const handleFieldChange = (fieldIndex: number, field: Partial<EditableField>) => {
    const updatedField = { ...editableFields[fieldIndex], ...field };

    // When type changes, reset other properties.
    if (field.type) {
      updatedField.format = undefined;
      updatedField.enum = undefined;
      updatedField.enumInput = '';
      updatedField.$ref = undefined;
      // Reset array items when type changes away from array
      if (field.type !== 'array') {
        updatedField.itemsType = undefined;
        updatedField.itemsRef = undefined;
        updatedField.itemsFormat = undefined;
      } else if (!updatedField.itemsType) {
        // Set default itemsType when changing to array
        updatedField.itemsType = 'string';
      }
    }

    // An item format arriving with no item type settles the panel's `?? 'string'` display.
    if (field.itemsFormat && !updatedField.itemsType) {
      updatedField.itemsType = 'string';
    }

    // The item type owns the reference and the format; each dies with it.
    if (field.itemsType) {
      if (field.itemsType !== 'reference') updatedField.itemsRef = undefined;
      if (field.itemsType !== 'string') updatedField.itemsFormat = undefined;
    }

    setEditableFields(editableFields.map((f, index) => (index === fieldIndex ? updatedField : f)));
    markDirty();

    // Mirror the change into the draft schema immediately.
    if (editedSchema) {
      // Use editableFields to get the old name to ensure we're referencing the correct field
      // even if the schema property order has changed (e.g. due to previous renames/adds)
      const oldFieldName = editableFields[fieldIndex].name;

      // The rewrite below drops any key it does not build, PII annotations included.
      const existingProperty = (editedSchema.schema.properties as Record<string, unknown> | undefined)?.[
        oldFieldName
      ] as Record<string, unknown> | undefined;
      const existingItems = existingProperty?.items as Record<string, unknown> | undefined;
      const piiAnnotation = existingProperty?.['x-pii-type']
        ? { 'x-pii-type': existingProperty['x-pii-type'] }
        : {};
      const itemsPiiAnnotation = existingItems?.['x-pii-type']
        ? { 'x-pii-type': existingItems['x-pii-type'] }
        : {};

      // Update the schema structure
      const updatedSchema = { ...editedSchema.schema } as Record<string, unknown>;

      // If field name changed, remove old field
      if (field.name && oldFieldName !== field.name) {
        // Create a shallow copy of properties to avoid mutating the original object
        const properties = {
          ...(updatedSchema.properties as Record<string, unknown>),
        };
        updatedSchema.properties = properties;

        delete properties[oldFieldName];
        const required = updatedSchema.required as string[];
        if (required) {
          updatedSchema.required = required.filter((name: string) => name !== oldFieldName);
        }
      }

      // Add/update field
      // Create a shallow copy of properties if we haven't already (for non-rename updates)
      const properties =
        updatedSchema.properties === editedSchema.schema.properties
          ? { ...(updatedSchema.properties as Record<string, unknown>) }
          : (updatedSchema.properties as Record<string, unknown>);
      updatedSchema.properties = properties;

      const newFieldName = field.name || oldFieldName;

      if (updatedField.type === 'reference') {
        properties[newFieldName] = {
          $ref: updatedField.$ref,
          description: updatedField.description,
          ...piiAnnotation,
        };
      } else if (updatedField.type === 'array') {
        const propDef: Record<string, unknown> = {
          type: 'array',
          description: updatedField.description,
          ...piiAnnotation,
        };

        if (updatedField.itemsType === 'reference') {
          // `reference` is this editor's own type, not a JSON Schema one, so an
          // item with no schema picked yet stays a bare `$ref`-less object.
          propDef.items = {
            $ref: updatedField.itemsRef,
            ...itemsPiiAnnotation,
          };
        } else if (updatedField.itemsType) {
          const items: Record<string, unknown> = {
            type: updatedField.itemsType,
            ...itemsPiiAnnotation,
          };
          if (updatedField.itemsFormat && updatedField.itemsType === 'string') {
            items.format = updatedField.itemsFormat;
          }
          propDef.items = items;
        } else {
          propDef.items = { type: 'string', ...itemsPiiAnnotation };
        }

        properties[newFieldName] = propDef;
      } else {
        properties[newFieldName] = {
          type: updatedField.type,
          description: updatedField.description,
          ...(updatedField.format && { format: updatedField.format }),
          ...(updatedField.enum && updatedField.enum.length > 0 && { enum: updatedField.enum }),
          ...piiAnnotation,
        };
      }

      // Update required array
      if (updatedField.required) {
        let required = updatedSchema.required as string[];
        if (!required) {
          required = [];
          updatedSchema.required = required;
        }
        if (!required.includes(newFieldName)) {
          required.push(newFieldName);
        }
      } else {
        const required = updatedSchema.required as string[];
        if (required) {
          updatedSchema.required = required.filter((name: string) => name !== newFieldName);
        }
      }

      setEditedSchema({
        ...editedSchema,
        schema: updatedSchema,
      });

      // Update field mappings if field name changed
      if (field.name && oldFieldName !== field.name) {
        setFieldMappings(prev =>
          prev.map(mapping =>
            mapping.fieldName === oldFieldName
              ? { ...mapping, fieldName: field.name!, jsonPath: field.name! }
              : mapping,
          ),
        );

        // Update fieldExposure to use new field name
        setFieldExposure(prev => {
          const newExposure = { ...prev };
          if (newExposure[oldFieldName]) {
            newExposure[field.name!] = {
              ...newExposure[oldFieldName],
              // Preserve name when field is renamed
            };
            delete newExposure[oldFieldName];
          }
          return newExposure;
        });
      }
    }
  };

  // ── Extracted draft-edit handlers (previously inline in the render) ──────────

  // Everything derived from the draft resyncs against the pasted properties.
  const handleApplySchemaJson = (schema: Record<string, unknown>) => {
    if (!editedSchema) return;

    const schemaProperties = extractSchemaProperties(schema);
    const propertyNames = new Set(schemaProperties.map(property => property.name));

    setEditedSchema({ ...editedSchema, schema });

    setEditableFields(
      schemaProperties.map(property => ({
        name: property.name,
        type: property.type,
        description: property.description || fieldExposure[property.name]?.description || '',
        format: property.format,
        required: property.required,
        enum: property.enum,
        $ref: property.$ref,
        itemsType: property.itemsType,
        itemsRef: property.itemsRef,
        itemsFormat: property.itemsFormat,
        isEditing: false,
      })),
    );

    setFieldMappings(previous => {
      const survivingMappings = previous.filter(mapping => propertyNames.has(mapping.fieldName));
      const mappedNames = new Set(survivingMappings.map(mapping => mapping.fieldName));
      const newMappings = schemaProperties
        .filter(property => !mappedNames.has(property.name))
        .map(property => ({
          fieldName: property.name,
          mappingTypes: ['none'],
          jsonPath: `$.${property.name}`,
        }));
      return [...survivingMappings, ...newMappings];
    });

    // Filter conditions address nested values with dot notation, so a condition
    // survives as long as the property it starts from does.
    setFilterConditions(previous =>
      previous.filter(condition => propertyNames.has(jsonPathToTopLevelPropertyName(condition.field))),
    );

    setSelectedFieldName(current => (current && propertyNames.has(current) ? current : null));
    markDirty();
    setIsJsonDialogOpen(false);
  };

  const handleDescriptionChange = (description: string) => {
    if (!editedSchema) return;
    markDirty();
    setEditedSchema({ ...editedSchema, description });
  };

  const handleDisplayNameChange = (exposeNameJsonPath: string) => {
    if (!editedSchema) return;
    markDirty();
    setEditedSchema({ ...editedSchema, exposeNameJsonPath: exposeNameJsonPath || undefined });
  };

  // Writes the URL pattern onto the correct mapping source (contacts primary /
  // additional, or the transaction entry) in the mappings draft.
  const handleUrlPatternChange = (newUrlPattern: string) => {
    if (!selectedSchema) return;
    markDirty();
    setUrlPatternInput(newUrlPattern);

    if (!mappings) return;

    const base: SchemaMappingsType = editedMappings ?? { type: 'combined_schema' };

    if (selectedSchema.category === 'contacts_schema' && mappings.contactsSchema) {
      const contactsSchema = base.contactsSchema
        ? {
            primarySource: { ...base.contactsSchema.primarySource },
            additionalSources: base.contactsSchema.additionalSources?.map(src => ({ ...src })),
          }
        : {
            primarySource: { ...mappings.contactsSchema.primarySource },
            additionalSources: mappings.contactsSchema.additionalSources?.map(src => ({ ...src })),
          };

      if (contactSourceType === 'primary') {
        contactsSchema.primarySource.urlPattern = newUrlPattern;
      } else if (contactsSchema.additionalSources) {
        contactsSchema.additionalSources[additionalSourceIndex] = {
          ...contactsSchema.additionalSources[additionalSourceIndex],
          urlPattern: newUrlPattern,
        };
      }
      setEditedMappings({ ...base, contactsSchema });
    } else if (selectedSchema.category === 'transactions_schema' && mappings.transactionsSchema) {
      const source = base.transactionsSchema ?? mappings.transactionsSchema;
      const transactionsSchema = source.map(txn =>
        txn.schemaId === selectedSchemaId ? { ...txn, urlPattern: newUrlPattern } : txn,
      );
      setEditedMappings({ ...base, transactionsSchema });
    }
  };

  const handleExposureToggle = (
    fieldName: string,
    surface: 'segments' | 'mergeFields' | 'agentTools',
    value: boolean,
  ) => {
    markDirty();
    setFieldExposure(prev => ({
      ...prev,
      [fieldName]: {
        segments: prev[fieldName]?.segments || false,
        mergeFields: prev[fieldName]?.mergeFields || false,
        agentTools: prev[fieldName]?.agentTools || false,
        name: prev[fieldName]?.name || '',
        description: prev[fieldName]?.description,
        [surface]: value,
      },
    }));
  };

  const handleExposureNameChange = (fieldName: string, name: string) => {
    markDirty();
    setFieldExposure(prev => ({
      ...prev,
      [fieldName]: {
        segments: prev[fieldName]?.segments || false,
        mergeFields: prev[fieldName]?.mergeFields || false,
        agentTools: prev[fieldName]?.agentTools || false,
        description: prev[fieldName]?.description,
        name,
      },
    }));
  };

  // Sets the status field's active/inactive value translation (contacts schema).
  const handleStatusValueChange = (
    field: string,
    which: 'activeValue' | 'deactivatedValue',
    value: string,
  ) => {
    if (!editedMappings?.contactsSchema) return;
    markDirty();
    const currentStatus = editedMappings.contactsSchema.primarySource?.status as StatusField | undefined;
    const updatedStatus = {
      field: currentStatus?.field || field,
      activeValue: which === 'activeValue' ? value : currentStatus?.activeValue || '',
      deactivatedValue: which === 'deactivatedValue' ? value : currentStatus?.deactivatedValue || '',
    };
    setEditedMappings({
      ...editedMappings,
      contactsSchema: {
        ...editedMappings.contactsSchema,
        primarySource: { ...editedMappings.contactsSchema.primarySource, status: updatedStatus },
      },
    });
  };

  // Sets the transaction amount's currency field / currency code (mutually exclusive).
  const handleAmountChange = (which: 'currencyField' | 'currency', value: string) => {
    markDirty();
    const initialTransactions = editedMappings?.transactionsSchema ?? mappings?.transactionsSchema ?? [];
    const transactionsSchema = initialTransactions.map(txn =>
      txn.schemaId === selectedSchemaId
        ? {
            ...txn,
            amount: {
              field: (txn.amount as TransactionAmount | undefined)?.field || '',
              currencyField: which === 'currencyField' ? value : undefined,
              currency: which === 'currency' ? (value as CurrencyCode) : undefined,
            },
          }
        : txn,
    );
    setEditedMappings({ type: 'combined_schema', ...editedMappings, transactionsSchema });
  };

  const extractSchemaProperties = (schema: Record<string, unknown>): SchemaProperty[] => {
    const properties = (schema.properties as Record<string, unknown>) || {};
    const required = (schema.required as string[]) || [];

    if (!properties || Object.keys(properties).length === 0) {
      return [];
    }

    return Object.entries(properties).map(([name, prop]) => {
      const propObj = prop as Record<string, unknown>;
      const ref = propObj.$ref as string;

      if (ref && ref.startsWith('reach:schemas/')) {
        return {
          name,
          type: 'reference',
          $ref: ref,
          description: (propObj.description as string) || '',
          required: required.includes(name),
          enum: undefined,
          format: undefined,
        };
      }

      const baseProperty = {
        name,
        type: (propObj.type || 'string') as JsonSchemaType,
        description: (propObj.description as string) || '',
        format: propObj.format ? (propObj.format as JsonSchemaFormat) : undefined,
        required: required.includes(name),
        enum: propObj.enum ? (propObj.enum as string[]) : undefined,
        $ref: undefined,
      };

      if (propObj.type === 'array' && propObj.items) {
        const items = propObj.items as Record<string, unknown>;
        if (items.$ref && typeof items.$ref === 'string' && items.$ref.startsWith('reach:schemas/')) {
          return {
            ...baseProperty,
            itemsType: 'reference',
            itemsRef: items.$ref as string,
          };
        } else if (items.type) {
          return {
            ...baseProperty,
            itemsType: items.type as ArrayItemType,
            itemsFormat: items.format ? (items.format as JsonSchemaFormat) : undefined,
          };
        }
      }

      return baseProperty;
    });
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      contacts_schema: 'Contacts',
      transactions_schema: 'Transactions',
      locations_schema: 'Locations',
      custom_schema: 'Custom',
      combined_schema: 'Combined',
      customer_fields: 'Customer Fields',
    };
    return labels[category] || category;
  };

  const buildFieldsToExpose = (schema: Record<string, unknown>): FieldToExpose[] => {
    const schemaProperties = extractSchemaProperties(schema);
    return buildFieldsToExposeFromState({ fieldExposure, schemaProperties });
  };

  const getMappingOptions = (
    schemaCategory: string,
  ): Array<{ value: string; label: string; required?: boolean }> => {
    const baseOptions = [{ value: 'none', label: 'No Mapping' }];

    switch (schemaCategory) {
      case 'contacts_schema':
        return [
          ...baseOptions,
          { value: 'email', label: 'Email' },
          { value: 'phone', label: 'Phone' },
          { value: 'userId', label: 'User ID', required: true },
          { value: 'firstName', label: 'First Name' },
          { value: 'lastName', label: 'Last Name' },
          { value: 'fullName', label: 'Full Name' },
          { value: 'location', label: 'Location ID' },
          { value: 'pointOfContactName', label: 'Point-of-contact name' },
          { value: 'pointOfContactEmail', label: 'Point-of-contact email' },
          { value: 'status', label: 'Status' },
          { value: 'createdDate', label: 'Created Date', required: true },
          { value: 'emailOptOut', label: 'Email Opt Out' },
          { value: 'emailOptIn', label: 'Email Opt In' },
          { value: 'emailOptOutTimestamp', label: 'Email Opt Out Timestamp' },
          { value: 'smsOptOut', label: 'SMS Opt Out' },
          { value: 'smsOptIn', label: 'SMS Opt In' },
          { value: 'smsOptOutTimestamp', label: 'SMS Opt Out Timestamp' },
          { value: 'hipaaConsent', label: 'HIPAA Consent' },
        ];

      case 'locations_schema':
        return [
          ...baseOptions,
          { value: 'id_field', label: 'ID Field', required: true },
          { value: 'name', label: 'Name' },
          { value: 'address_line_1', label: 'Address Line 1' },
          { value: 'address_line_2', label: 'Address Line 2' },
          { value: 'address_line_3', label: 'Address Line 3' },
          { value: 'locality', label: 'City' },
          { value: 'district', label: 'State/Province' },
          { value: 'postal_code', label: 'Postal Code' },
          { value: 'country_code', label: 'Country Code' },
          { value: 'phone', label: 'Phone' },
          { value: 'email', label: 'Email' },
          { value: 'website', label: 'Website' },
        ];

      case 'transactions_schema':
        return [
          ...baseOptions,
          { value: 'id_field', label: 'ID Field', required: true },
          { value: 'userId', label: 'User ID', required: true },
          { value: 'amount', label: 'Amount' },
          { value: 'dates.createdDate', label: 'Created Date' },
          { value: 'dates.paidDate', label: 'Paid Date' },
          { value: 'dates.dueDate', label: 'Due Date' },
          { value: 'dates.startDate', label: 'Start Date' },
          { value: 'dates.endDate', label: 'End Date' },
          { value: 'locationId', label: 'Location ID' },
        ];

      default:
        return baseOptions;
    }
  };

  const fieldPendingRemovalMapping = fieldPendingRemoval
    ? fieldMappings.find(mapping => mapping.fieldName === fieldPendingRemoval.name)
    : undefined;
  const fieldPendingRemovalHasMapping =
    fieldPendingRemovalMapping?.mappingTypes.some(mappingType => mappingType !== 'none') ?? false;
  const anyFieldExposesAgentTools = Object.values(fieldExposure).some(exposure => exposure.agentTools);
  // Agentic Tools UI stays visible for a field that is already exposed to it, so
  // saved exposure remains clearable for partners without the flag.
  const showAgentToolsGuidance = agentToolsEnabled || anyFieldExposesAgentTools;
  const fieldPendingRemovalExposure = fieldPendingRemoval
    ? fieldExposure[fieldPendingRemoval.name]
    : undefined;
  const fieldPendingRemovalHasExposure = hasFieldExposure(fieldPendingRemovalExposure);
  const fieldPendingRemovalExposureSurfaces = [
    fieldPendingRemovalExposure?.segments ? 'segments' : null,
    fieldPendingRemovalExposure?.mergeFields ? 'merge fields' : null,
    fieldPendingRemovalExposure?.agentTools ? 'Agentic Tools' : null,
  ].filter(surface => surface !== null);
  const fieldPendingRemovalExposureLabel = `Exposed to ${fieldPendingRemovalExposureSurfaces.join(', ')}`;
  const fieldPendingRemovalHasFilterConditions = fieldPendingRemoval
    ? filterConditions.some(condition => condition.field === fieldPendingRemoval.name)
    : false;

  // ── Derived view state for the redesigned layout ────────────────────────────
  const activeSchema = selectedSchema;
  const category = activeSchema?.category ?? '';
  const draftSchemaObject = editedSchema?.schema ?? activeSchema?.schema ?? {};
  const draftProperties = extractSchemaProperties(draftSchemaObject);
  const displayNameOptions = draftProperties.map(p => ({ value: `.${p.name}`, label: p.name }));
  const displayNameValue =
    editedSchema?.exposeNameJsonPath ||
    ((editedSchema as unknown as Record<string, unknown>)?.json_path_expose_name as
      | string
      | undefined) ||
    '';
  const showUrlPattern = category === 'contacts_schema' || category === 'transactions_schema';
  const versionNumber = activeSchema?.versionNumber ?? activeSchema?.version_number;
  const isSaving = updateSchemaMutation.isPending || updateMappingsMutation.isPending;

  // Per-attribute dirty tracking. The saved fingerprints are re-captured on every
  // clean commit (initial hydrate, Discard, successful save), so anything that
  // differs while the draft is dirty is an edit the user has not persisted yet.
  const attributeSignatures = useMemo(() => {
    const signatures: Record<string, string> = {};
    editableFields.forEach(field => {
      signatures[field.name] = attributeSignature(
        field,
        fieldMappings.find(mapping => mapping.fieldName === field.name),
        fieldExposure[field.name],
      );
    });
    return signatures;
  }, [editableFields, fieldMappings, fieldExposure]);

  useEffect(() => {
    if (!hasUnsavedChanges) savedAttributeSignaturesRef.current = attributeSignatures;
  }, [hasUnsavedChanges, attributeSignatures]);

  const dirtyAttributeNames = hasUnsavedChanges
    ? new Set(
        Object.keys(attributeSignatures).filter(
          name => attributeSignatures[name] !== savedAttributeSignaturesRef.current[name],
        ),
      )
    : EMPTY_NAME_SET;

  const normalizedQuery = attributeQuery.trim().toLowerCase();
  const attributeRows = editableFields
    .filter(field => !normalizedQuery || field.name.toLowerCase().includes(normalizedQuery))
    .map(field => {
      const mapping = fieldMappings.find(m => m.fieldName === field.name);
      const mappingTypes = mapping?.mappingTypes.filter(t => t !== 'none') ?? [];
      const options = getMappingOptions(category);
      const mappingLabels = mappingTypes.map(t => options.find(o => o.value === t)?.label ?? t);
      let typeLabel: string = field.type;
      const isReference =
        field.type === 'reference' || (field.type === 'array' && field.itemsType === 'reference');
      if (field.type === 'reference') {
        typeLabel = `Reference (${resolveReferenceLabel(field.$ref, schemas)?.name ?? 'Unknown'})`;
      } else if (field.type === 'array' && field.itemsType) {
        typeLabel =
          field.itemsType === 'reference'
            ? `array of ${resolveReferenceLabel(field.itemsRef, schemas)?.name ?? 'Unknown'}`
            : `array of ${field.itemsType}`;
      }
      const exposureEntry = fieldExposure[field.name];
      const enumMeta = field.enum && field.enum.length > 0 ? 'enum' : undefined;
      return {
        name: field.name,
        required: field.required,
        description: field.description || exposureEntry?.description || '',
        typeLabel,
        isReference,
        typeMeta: field.format || enumMeta,
        mappingLabels,
        exposure: {
          segments: exposureEntry?.segments ?? false,
          mergeFields: exposureEntry?.mergeFields ?? false,
          agentTools: exposureEntry?.agentTools ?? false,
        },
        exposureNamed: !!exposureEntry?.name?.trim(),
        dirty: dirtyAttributeNames.has(field.name),
      };
    });

  const selectedFieldIndex = editableFields.findIndex(field => field.name === selectedFieldName);
  const selectedField = selectedFieldIndex >= 0 ? editableFields[selectedFieldIndex] : null;
  const selectedIsArray = selectedField?.type === 'array';
  const selectedIsArrayOfReference = selectedIsArray && selectedField?.itemsType === 'reference';
  const selectedMapping = selectedField
    ? fieldMappings.find(m => m.fieldName === selectedField.name)
    : undefined;
  const selectedMappingTypes = selectedMapping?.mappingTypes.filter(t => t !== 'none') ?? [];
  const mappingOptionList = getMappingOptions(category);
  const mappingChips = selectedMappingTypes.map(type => {
    const option = mappingOptionList.find(o => o.value === type);
    return { type, label: option?.label ?? type, required: option?.required };
  });
  const addMappingOptions = mappingOptionList.filter(
    option => option.value !== 'none' && !selectedMapping?.mappingTypes.includes(option.value),
  );
  const selectedExposure = selectedField ? fieldExposure[selectedField.name] : undefined;
  const selectedExposureState = {
    segments: selectedExposure?.segments ?? false,
    mergeFields: selectedExposure?.mergeFields ?? false,
    agentTools: selectedExposure?.agentTools ?? false,
  };
  const selectedExposureName = selectedExposure?.name ?? '';
  // Merge fields resolve by their exposure display name as a single flat token
  // (see useTemplateMergeFields) — not a dotted schema.field path.
  const selectedMergeToken = selectedExposureName.trim() ? `{{${selectedExposureName.trim()}}}` : '';
  const selectedExposureHasAny =
    selectedExposureState.segments ||
    selectedExposureState.mergeFields ||
    selectedExposureState.agentTools;
  const statusFieldValue = (editedMappings?.contactsSchema?.primarySource?.status ??
    mappings?.contactsSchema?.primarySource?.status) as StatusField | undefined;
  const amountValue = (editedMappings?.transactionsSchema?.find(t => t.schemaId === selectedSchemaId)
    ?.amount ?? mappings?.transactionsSchema?.find(t => t.schemaId === selectedSchemaId)?.amount) as
    | TransactionAmount
    | undefined;
  const showStatusValues = category === 'contacts_schema' && selectedMappingTypes.includes('status');
  const showAmountValues = category === 'transactions_schema' && selectedMappingTypes.includes('amount');
  const referenceSchemaOptions = schemas
    .filter(s => s.id !== selectedSchemaId)
    .map(s => ({ value: `reach:schemas/${s.id}`, label: s.name }));

  const applySchemaSwitch = (schemaId: string) => {
    setSelectedSchemaId(schemaId);
    setSelectedFieldName(null);
    setAttributeQuery('');
    const params = new URLSearchParams(searchParams);
    if (schemaId) {
      params.set('schema', schemaId);
    } else {
      params.delete('schema');
    }
    setSearchParams(params, { replace: true });
  };

  // Guards schema navigation behind a discard confirmation when the draft is dirty.
  const handleSelectSchema = (schemaId: string) => {
    if (schemaId === selectedSchemaId) return;
    if (hasUnsavedChanges) {
      setPendingSchemaSwitch(schemaId);
      return;
    }
    applySchemaSwitch(schemaId);
  };

  // Contacts-only assignment status shown in the schema header.
  let assignmentNode: React.ReactNode = null;
  if (activeSchema && category === 'contacts_schema') {
    const isPrimary = mappings?.contactsSchema?.primarySource?.schemaId === activeSchema.id;
    const additionalIndex =
      mappings?.contactsSchema?.additionalSources?.findIndex(src => src.schemaId === activeSchema.id) ??
      -1;
    const isAssigned = isPrimary || additionalIndex !== -1;
    if (pendingAssignment) {
      const pendingLabel = pendingAssignment === 'primary' ? 'Primary Contact' : 'Additional Contact';
      assignmentNode = <span className={styles.schemaAssignmentPending}>{pendingLabel} (unsaved)</span>;
    } else if (isAssigned) {
      const assignedLabel = isPrimary ? 'Primary Contact' : `Additional Contact #${additionalIndex + 1}`;
      assignmentNode = (
        <span className={styles.schemaAssignmentInfo}>
          {assignedLabel}
          {!isPrimary && (
            <button
              type="button"
              className={styles.schemaAssignmentLink}
              onClick={() => setShowSwapWarning(true)}>
              Promote to Primary
            </button>
          )}
        </span>
      );
    } else {
      assignmentNode = (
        <span className={styles.schemaAssignmentInfo}>
          Not assigned
          <button
            type="button"
            className={styles.schemaAssignmentLink}
            onClick={() => setShowAssignSchemaDialog(true)}>
            Assign to Contacts
          </button>
        </span>
      );
    }
  }

  if (schemasLoading || mappingsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className={styles.schemaEditorLayout}>
      <div className={styles.schemaEditorMainPane}>
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <Check className="h-5 w-5 text-green-600 mr-2" />
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          </div>
        )}
        {schemaSuccessMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <Check className="h-5 w-5 text-green-600 mr-2" />
              <p className="text-sm text-green-800">{schemaSuccessMessage}</p>
            </div>
          </div>
        )}
        {mappingSuccessMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <Check className="h-5 w-5 text-green-600 mr-2" />
              <p className="text-sm text-green-800">{mappingSuccessMessage}</p>
            </div>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">{error}</p>
                {errorDetails.length > 0 && (
                  <ul className="mt-2 space-y-1 text-sm text-red-700 list-disc list-inside">
                    {errorDetails.map((detail, index) => (
                      <li key={index}>{detail}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
        {schemaError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Schema update failed: {schemaError}</p>
                {errorDetails.length > 0 && (
                  <ul className="mt-2 space-y-1 text-sm text-red-700 list-disc list-inside">
                    {errorDetails.map((detail, index) => (
                      <li key={index}>{detail}</li>
                    ))}
                  </ul>
                )}
                {schemaLookupConflicts.length > 0 && (
                  <SchemaLookupConflictList conflicts={schemaLookupConflicts} />
                )}
              </div>
            </div>
          </div>
        )}
        {mappingError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Mapping update failed: {mappingError}</p>
                {mappingErrorDetails.length > 0 && (
                  <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                    {mappingErrorDetails.map((detail, index) => (
                      <li key={index}>{detail}</li>
                    ))}
                  </ul>
                )}
                {mappingLookupConflicts.length > 0 && (
                  <SchemaLookupConflictList conflicts={mappingLookupConflicts} />
                )}
              </div>
            </div>
          </div>
        )}

        {!selectedSchemaId ? (
          <SchemaEmptyState
            schemas={schemas}
            onSelect={handleSelectSchema}
            onCreate={() => setIsCreateDialogOpen(true)}
          />
        ) : schemaLoading ? (
          <div className={styles.schemaEditorLoadingPane}>
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : activeSchema ? (
          <>
            <div className={styles.schemaEditorHeader}>
              <div className={styles.schemaEditorHeaderText}>
                <SchemaIconColorPicker
                  value={activeSchema.displayMetadata ?? activeSchema.display_metadata}
                  disabled={updateSchemaMutation.isPending}
                  triggerLabel={`Set icon and color for ${activeSchema.name}`}
                  triggerSize={22}
                  onApply={handleApplyDisplayMetadata}
                />
                <SchemaSwitcher
                  schemas={schemas}
                  selectedSchemaId={selectedSchemaId}
                  onSelect={handleSelectSchema}
                  onDeselect={() => handleSelectSchema('')}
                  onCreate={() => setIsCreateDialogOpen(true)}
                />
                <span className={styles.schemaCategoryPill}>{getCategoryLabel(category)}</span>
                {versionNumber != null && (
                  <span className={styles.schemaVersionTag}>v{versionNumber}</span>
                )}
                {assignmentNode}
              </div>
              <div className={styles.schemaEditorHeaderActions}>
                <button
                  type="button"
                  className={styles.schemaEditorDangerButton}
                  onClick={() => setIsDeactivateDialogOpen(true)}>
                  <Trash2 size={14} />
                  Deactivate
                </button>
                <button
                  type="button"
                  className={styles.schemaEditorSecondaryButton}
                  onClick={() => setIsJsonDialogOpen(true)}
                  title="Edit the raw JSON Schema for this draft">
                  <Braces size={14} />
                  Edit JSON
                </button>
                <button
                  type="button"
                  className={styles.schemaEditorSecondaryButton}
                  onClick={handleDiscard}
                  disabled={!hasUnsavedChanges}
                  title="Throw away your unsaved edits and reload the saved schema">
                  <Undo2 size={14} />
                  Discard changes
                </button>
                <button
                  type="button"
                  className={styles.schemaEditorPrimaryButton}
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || isSaving}>
                  {isSaving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                  Save
                </button>
              </div>
            </div>

            <SchemaMetaCard
              schema={activeSchema}
              category={getCategoryLabel(category)}
              descriptionValue={editedSchema?.description ?? ''}
              onDescriptionChange={handleDescriptionChange}
              showUrlPattern={showUrlPattern}
              urlPatternValue={urlPatternInput}
              onUrlPatternChange={handleUrlPatternChange}
              displayNameValue={displayNameValue}
              displayNameOptions={displayNameOptions}
              onDisplayNameChange={handleDisplayNameChange}
            />

            {showAgentToolsGuidance && (
              <p className={styles.fieldExposureNote}>
                Agentic Tools exposure is schema-wide: any lookup tool using this schema can return
                enabled attributes. Only expose sensitive values, such as gate codes, when agents are
                allowed to disclose them.
              </p>
            )}

            <AttributesTable
              rows={attributeRows}
              showAgentTools={agentToolsEnabled}
              count={editableFields.length}
              query={attributeQuery}
              onQueryChange={setAttributeQuery}
              onAddAttribute={handleAddAttribute}
              onSelect={setSelectedFieldName}
              selectedName={selectedFieldName}
            />

            {category === 'transactions_schema' && (
              <div className={styles.filterCard}>
                <div className={styles.filterHeader}>Transaction filter</div>
                <div className={styles.filterBody}>
                  <p className={styles.filterHelp}>
                    Define conditions to filter which transactions are included in Reach metrics and
                    features. Use dot notation for nested fields; wrap values in quotes for string
                    literals.
                  </p>
                  <div className={styles.filterLogicRow}>
                    <select
                      className={styles.filterSelect}
                      value={filterLogic}
                      onChange={e => handleFilterLogicChange(e.target.value as 'and' | 'or')}>
                      <option value="and">All of</option>
                      <option value="or">Any of</option>
                    </select>
                    <span className={styles.filterHelp}>the following conditions:</span>
                  </div>
                  {filterConditions.map(condition => (
                    <div key={condition.id} className={styles.filterConditionRow}>
                      <input
                        className={styles.filterInput}
                        value={condition.field}
                        onChange={e => updateFilterCondition(condition.id, { field: e.target.value })}
                        placeholder="Field name (e.g., serviceType.isVisit)"
                      />
                      <select
                        className={styles.filterSelect}
                        value={condition.operator}
                        onChange={e =>
                          updateFilterCondition(condition.id, { operator: e.target.value })
                        }>
                        <option value="==">equals</option>
                        <option value="!=">not equals</option>
                        <option value=">">greater than</option>
                        <option value="<">less than</option>
                        <option value=">=">greater or equal</option>
                        <option value="<=">less or equal</option>
                        <option value="in">in</option>
                        <option value="!in">not in</option>
                        <option value="array_contains">array contains</option>
                      </select>
                      <input
                        className={styles.filterInput}
                        value={condition.value}
                        onChange={e => updateFilterCondition(condition.id, { value: e.target.value })}
                        placeholder={
                          condition.operator === 'array_contains'
                            ? 'Comma-separated values'
                            : 'Value (use "quotes" for strings)'
                        }
                      />
                      <button
                        type="button"
                        className={styles.filterRemove}
                        onClick={() => removeFilterCondition(condition.id)}
                        aria-label="Remove condition">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className={styles.schemaEditorSecondaryButton}
                    onClick={addFilterCondition}>
                    <Plus size={14} />
                    Add condition
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          // The selected schema id points at nothing loadable (e.g. a stale
          // ?schema=<deleted-id> URL). Fall back to the picker so the user isn't
          // stranded — the switcher only renders inside the activeSchema branch.
          <SchemaEmptyState
            schemas={schemas}
            onSelect={handleSelectSchema}
            onCreate={() => setIsCreateDialogOpen(true)}
          />
        )}
      </div>

      {selectedField && (
        <AttributeEditorPanel
          kicker="Attribute"
          fieldName={selectedField.name}
          onNameChange={value => {
            handleFieldChange(selectedFieldIndex, { name: value });
            setSelectedFieldName(value);
          }}
          type={selectedField.type}
          onTypeChange={value => handleFieldChange(selectedFieldIndex, { type: value })}
          itemsType={selectedIsArray ? (selectedField.itemsType ?? 'string') : ''}
          onItemsTypeChange={value => handleFieldChange(selectedFieldIndex, { itemsType: value })}
          format={(selectedIsArray ? selectedField.itemsFormat : selectedField.format) ?? ''}
          onFormatChange={value =>
            handleFieldChange(selectedFieldIndex, {
              [selectedIsArray ? 'itemsFormat' : 'format']: (value || undefined) as
                | JsonSchemaFormat
                | undefined,
            })
          }
          showReference={selectedField.type === 'reference' || selectedIsArrayOfReference}
          referenceValue={
            (selectedIsArrayOfReference ? selectedField.itemsRef : selectedField.$ref) ?? ''
          }
          schemaOptions={referenceSchemaOptions}
          onReferenceChange={value =>
            handleFieldChange(
              selectedFieldIndex,
              selectedIsArrayOfReference ? { itemsRef: value } : { $ref: value },
            )
          }
          enumValue={selectedField.enumInput ?? selectedField.enum?.join(', ') ?? ''}
          onEnumChange={value =>
            handleFieldChange(selectedFieldIndex, {
              enumInput: value,
              enum: value
                ? value
                    .split(',')
                    .map(part => part.trim())
                    .filter(Boolean)
                : undefined,
            })
          }
          required={selectedField.required}
          onRequiredChange={value => handleFieldChange(selectedFieldIndex, { required: value })}
          mappingChips={mappingChips}
          addMappingOptions={addMappingOptions}
          onAddMapping={type => handleAddMapping(selectedField.name, type)}
          onRemoveMapping={type => handleRemoveMapping(selectedField.name, type)}
          showStatusValues={showStatusValues}
          statusActiveValue={statusFieldValue?.activeValue ?? ''}
          statusInactiveValue={statusFieldValue?.deactivatedValue ?? ''}
          onStatusChange={(which, value) => handleStatusValueChange(selectedField.name, which, value)}
          showAmountValues={showAmountValues}
          amountCurrencyField={amountValue?.currencyField ?? ''}
          amountCurrency={amountValue?.currency ?? ''}
          amountFieldOptions={draftProperties.map(p => p.name)}
          currencyOptions={Object.values(CurrencyCode)}
          onAmountChange={handleAmountChange}
          description={selectedField.description ?? ''}
          onDescriptionChange={value => handleFieldChange(selectedFieldIndex, { description: value })}
          exposure={selectedExposureState}
          onExposureToggle={(surface, value) => handleExposureToggle(selectedField.name, surface, value)}
          exposureName={selectedExposureName}
          onExposureNameChange={value => handleExposureNameChange(selectedField.name, value)}
          exposureNameRequired={selectedExposureHasAny && !selectedExposureName.trim()}
          mergeToken={selectedMergeToken}
          showAgentTools={agentToolsEnabled}
          showAgentToolsHint={showAgentToolsGuidance}
          onRemove={() => setFieldPendingRemoval(selectedField)}
          onClose={() => setSelectedFieldName(null)}
          dirty={hasUnsavedChanges}
          saving={isSaving}
          onSave={handleSave}
        />
      )}

      {isJsonDialogOpen && editedSchema && (
        <SchemaJsonDialog
          schemaName={editedSchema.name}
          externalIdField={editedSchema.externalIdField ?? ''}
          value={JSON.stringify(editedSchema.schema, null, 2)}
          onApply={handleApplySchemaJson}
          onClose={() => setIsJsonDialogOpen(false)}
        />
      )}

      <CreateSchemaDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={data => createMutation.mutate(data)}
        isLoading={createMutation.isPending}
      />

      {showConflictDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-start mb-4">
              <AlertCircle className="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Mapped Fields Detected</h3>
                <p className="text-sm text-gray-600 mb-3">
                  The following fields are currently mapped but have no resources using them:
                </p>
                <ul className="text-sm text-gray-800 list-disc list-inside mb-4">
                  {conflictingFields.map((conflict, index) => (
                    <li key={index} className="font-medium">
                      {conflict.field}
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-gray-600">
                  We can temporarily remove these mappings, update the schema, and then restore your
                  mappings. This will allow the schema update to proceed.
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConflictDialog(false);
                  setConflictingFields([]);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleConflictResolution}
                disabled={updateSchemaMutation.isPending || updateMappingsMutation.isPending}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50">
                {updateSchemaMutation.isPending || updateMappingsMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 inline animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Proceed with Update'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {fieldPendingRemoval && (
        <div className={styles.removeFieldDialogOverlay}>
          <div
            className={styles.removeFieldDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-field-dialog-title">
            <div className={styles.removeFieldDialogHeader}>
              <Trash2 className={styles.removeFieldDialogIcon} />
              <h3 id="remove-field-dialog-title" className={styles.removeFieldDialogTitle}>
                Remove Field
              </h3>
            </div>
            <div className={styles.removeFieldDialogBody}>
              <p className={styles.removeFieldDialogPrimaryText}>
                Remove{' '}
                <span className={styles.removeFieldDialogFieldName}>{fieldPendingRemoval.name}</span>{' '}
                from this schema draft?
              </p>
              <p className={styles.removeFieldDialogSecondaryText}>
                This change will not be saved until you click Save. The save can still be blocked if the
                field is used by existing resources, mappings, segments, or merge fields.
              </p>
              {(fieldPendingRemoval.required ||
                fieldPendingRemovalHasMapping ||
                fieldPendingRemovalHasExposure ||
                fieldPendingRemovalHasFilterConditions) && (
                <div className={styles.removeFieldDialogWarning}>
                  <p className={styles.removeFieldDialogWarningTitle}>
                    This field is currently configured:
                  </p>
                  <ul className={styles.removeFieldDialogWarningList}>
                    {fieldPendingRemoval.required && <li>Marked as required in the schema</li>}
                    {fieldPendingRemovalHasMapping && <li>Used by schema mappings</li>}
                    {fieldPendingRemovalHasExposure && <li>{fieldPendingRemovalExposureLabel}</li>}
                    {fieldPendingRemovalHasFilterConditions && (
                      <li>Used by transaction filter conditions</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
            <div className={styles.removeFieldDialogActions}>
              <button
                type="button"
                onClick={() => setFieldPendingRemoval(null)}
                className={styles.removeFieldDialogCancelButton}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmFieldRemoval}
                className={styles.removeFieldDialogConfirmButton}>
                <Trash2 className={styles.removeFieldDialogConfirmIcon} />
                Remove Field
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssignSchemaDialog && activeSchema && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <FileJson className="h-6 w-6 text-primary mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                Assign {activeSchema.name} to Contacts
              </h3>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Choose how this schema should be used for contact data:
              </p>
              <div className="space-y-3">
                <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="assignmentType"
                    value="primary"
                    checked={assignmentType === 'primary'}
                    onChange={e => setAssignmentType(e.target.value as 'primary' | 'additional')}
                    className="mt-1 rounded-full border-gray-300 text-primary focus:ring-primary"
                    disabled={!mappings?.contactsSchema?.primarySource}
                  />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">Primary Contact Schema</div>
                    <div className="text-xs text-gray-500 mt-1">
                      Main source for contact information.{' '}
                      {mappings?.contactsSchema?.primarySource &&
                      mappings.contactsSchema.primarySource.schemaId !== activeSchema.id ? (
                        <span className="text-yellow-600 font-medium">
                          This will replace the current primary schema.
                        </span>
                      ) : null}
                    </div>
                  </div>
                </label>
                <label
                  className={`flex items-start p-3 border rounded-lg ${!mappings?.contactsSchema?.primarySource ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}>
                  <input
                    type="radio"
                    name="assignmentType"
                    value="additional"
                    checked={assignmentType === 'additional'}
                    onChange={e => setAssignmentType(e.target.value as 'primary' | 'additional')}
                    className="mt-1 rounded-full border-gray-300 text-primary focus:ring-primary"
                    disabled={!mappings?.contactsSchema?.primarySource}
                  />
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">Additional Contact Schema</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {!mappings?.contactsSchema?.primarySource ? (
                        <span className="text-red-600">You must set a primary schema first.</span>
                      ) : (
                        'Supplementary source for enriching contact data.'
                      )}
                    </div>
                  </div>
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAssignSchemaDialog(false);
                  setAssignmentType('primary');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={() => handleAssignSchema(assignmentType)}
                disabled={assignmentType === 'additional' && !mappings?.contactsSchema?.primarySource}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed">
                Assign Schema
              </button>
            </div>
          </div>
        </div>
      )}

      {showSwapWarning && activeSchema && mappings?.contactsSchema && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-start mb-4">
              <AlertCircle className="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Promote to Primary Contact</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Making this schema the Primary Contact will automatically demote the current primary
                  contact schema (
                  <strong>
                    {schemas.find(s => s.id === mappings.contactsSchema?.primarySource.schemaId)?.name ||
                      'Unknown'}
                  </strong>
                  ) to an Additional Contact source.
                </p>
                <p className="text-sm text-gray-500">Do you want to proceed?</p>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowSwapWarning(false);
                  setAssignmentType('primary');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={() => handleSwapAssignment('primary')}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-primary/90">
                Promote to Primary
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeactivateDialogOpen && activeSchema && (
        <ConfirmDialog
          title={`Deactivate schema "${activeSchema.name}"?`}
          confirmLabel={deactivateMutation.isPending ? 'Deactivating...' : 'Deactivate Schema'}
          onConfirm={() => deactivateMutation.mutate(activeSchema.id)}
          onClose={() => setIsDeactivateDialogOpen(false)}
          tone="destructive"
          disabled={deactivateMutation.isPending}>
          <p>
            This action is <strong>irreversible</strong>. The schema will be permanently deactivated and
            hidden from your schema list.
          </p>
          <p className="mt-2">
            You must first remove all dependencies — resources, schema mappings (including locations),
            and references from other schemas — that use this schema before it can be deactivated. The
            schema name becomes available for reuse once deactivation completes.
          </p>
        </ConfirmDialog>
      )}

      {pendingSchemaSwitch !== null && (
        <ConfirmDialog
          title="Discard unsaved changes?"
          confirmLabel="Discard changes"
          onConfirm={() => {
            const target = pendingSchemaSwitch;
            setPendingSchemaSwitch(null);
            if (target !== null) {
              applySchemaSwitch(target);
            }
          }}
          onClose={() => setPendingSchemaSwitch(null)}
          tone="destructive">
          <p>You have unsaved changes to this schema. Switching to another schema will discard them.</p>
        </ConfirmDialog>
      )}
    </div>
  );
};
